class YGDiscoverDesignResources extends HTMLElement {
  static dependenciesPromise = null;
  static pdfLibraryPromise = null;
  static productIdsPromises = new Map();

  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.handlePdfPreviewScroll = this.handlePdfPreviewScroll.bind(this);
    this.handleSearchInput = this.handleSearchInput.bind(this);
    this.handleSearchReset = this.handleSearchReset.bind(this);
    this.handleSearchSubmit = this.handleSearchSubmit.bind(this);
    this.handleTabTitleClick = this.handleTabTitleClick.bind(this);
    this.productsCache = new Map();
    this.remoteMimeTypeCache = new Map();
    this.fileTypeLookupPromises = new Map();
    this.currentCards = [];
    this.currentPageCards = [];
    this.allCards = [];
    this.searchFilteredCards = [];
    this.facetSelections = {
      resourceType: new Set(),
      fileType: new Set(),
      productType: new Set()
    };
    this.facetShowMore = {
      resourceType: false,
      fileType: false,
      productType: false
    };
    this.currentPage = 1;
    this.lastQuerySignature = '';
    this.renderRequestId = 0;
    this.filtersOpen = true;
    this.isInitialized = false;
    this.viewMode = 'grid';
    this.sortMode = 'relevant';
    this.gridScale = 3;
    this.zipLibLoaded = false;
    this.selectedFiles = new Map();
    this.previewRenderRequestId = 0;
    this.currentPreviewFile = null;
    this.currentPdfDocument = null;
    this.currentPdfLoadingTask = null;
    this.bodyOverflowBeforePreview = '';
    this.fileTypeRefreshFrame = 0;
    this.currentImagePreviewIndex = -1;
    this.tabActivationObserver = null;
    this.resourceLoadPromise = null;
    this.hasLoadedResources = false;
  }

  connectedCallback() {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    this.pdfPreviewMain?.addEventListener('scroll', this.handlePdfPreviewScroll, { passive: true });
    this.observeTabActivation();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    this.pdfPreviewMain?.removeEventListener('scroll', this.handlePdfPreviewScroll);
    this.searchInput?.removeEventListener('input', this.handleSearchInput);
    this.searchForm?.removeEventListener('reset', this.handleSearchReset);
    this.searchForm?.removeEventListener('submit', this.handleSearchSubmit);
    this.tabTitleContainer?.removeEventListener('click', this.handleTabTitleClick);
    this.tabActivationObserver?.disconnect();
    this.tabActivationObserver = null;
    this.closePreviewModal();
    this.cancelFileTypeRefresh();

  }

  async initialize() {
    if (this.hasLoadedResources) {
      return;
    }

    if (this.resourceLoadPromise) {
      return this.resourceLoadPromise;
    }

    this.resourceLoadPromise = this.loadResources();

    try {
      await this.resourceLoadPromise;
      this.hasLoadedResources = true;
    } catch {
      return;
    } finally {
      this.resourceLoadPromise = null;
    }
  }

  async loadResources() {
    try {
      this.filtersOpen = this.isMobileLayout() ? false : this.dataset.filtersOpen !== 'false';
      this.dataset.filtersOpen = this.filtersOpen ? 'true' : 'false';
      this.dataset.filtersInitialized = 'true';
      this.viewMode = this.dataset.viewMode || 'grid';
      this.sortMode = 'relevant';
      this.gridScale = Number.parseInt(this.gridScaleSlider?.value || this.dataset.gridScale || '3', 10) || 3;
      this.setViewMode(this.viewMode, false);
      this.setGridScale(this.gridScale);
      this.renderLoadingState();
      await this.loadDependencies();
      this.setupSearchBox();
      const productIds = await this.getAllProductIds();
      const products = await this.ensureProducts(productIds);

      this.allCards = this.buildCards(products);
      void this.resolveMissingCardFileTypes(this.allCards);
      this.applySearchFilter();
      this.applyFacets();
      this.renderFacets();
      this.syncFiltersToggle();
      this.updateSelectedCounter();
    } catch (error) {
      console.error('Failed to initialize YG Discover Design resources search.', error);
      this.renderErrorState();
      throw error;
    }
  }

  loadDependencies() {
    if (YGDiscoverDesignResources.dependenciesPromise) {
      return YGDiscoverDesignResources.dependenciesPromise;
    }

    YGDiscoverDesignResources.dependenciesPromise = this.loadScriptOnce(
      'algoliasearch-lite',
      'https://cdn.jsdelivr.net/npm/algoliasearch@5.53.0/dist/lite/builds/browser.umd.js'
    ).then(() => this.waitForGlobal(() => this.getAlgoliaSearchFactory()));

    return YGDiscoverDesignResources.dependenciesPromise;
  }

  loadScriptOnce(key, src) {
    const existingScript = document.querySelector(`script[data-yg-script="${key}"]`);
    const existingGlobal = this.getDependencyGlobal(key);

    if (existingScript) {
      if (existingScript.dataset.loaded === 'true' || existingGlobal) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        existingScript.addEventListener(
          'load',
          () => {
            existingScript.dataset.loaded = 'true';
            resolve();
          },
          { once: true }
        );
        existingScript.addEventListener(
          'error',
          () => reject(new Error(`Failed to load script: ${src}`)),
          { once: true }
        );
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.dataset.ygScript = key;
      script.onload = () => {
        script.dataset.loaded = 'true';
        resolve();
      };
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  getDependencyGlobal(key) {
    if (key === 'algoliasearch-lite') {
      return this.getAlgoliaSearchFactory();
    }

    return null;
  }

  waitForGlobal(getter, retries = 40, delay = 100) {
    return new Promise((resolve, reject) => {
      const check = (remainingRetries) => {
        const value = getter();

        if (value) {
          resolve(value);
          return;
        }

        if (remainingRetries <= 0) {
          reject(new Error('Expected dependency global was not available after script load.'));
          return;
        }

        window.setTimeout(() => check(remainingRetries - 1), delay);
      };

      check(retries);
    });
  }

  getAlgoliaSearchFactory() {
    const algoliaLite = window['algoliasearch/lite'];

    if (typeof window.algoliasearch === 'function') {
      return window.algoliasearch;
    }

    if (algoliaLite && typeof algoliaLite.liteClient === 'function') {
      return algoliaLite.liteClient;
    }

    if (typeof algoliaLite === 'function') {
      return algoliaLite;
    }

    return null;
  }

  setupSearchBox() {
    if (!this.searchBoxContainer || this.searchInput) {
      return;
    }

    this.searchBoxContainer.innerHTML = `
      <div class="ais-SearchBox">
        <form action="" role="search" class="ais-SearchBox-form" novalidate>
          <input
            class="ais-SearchBox-input"
            type="search"
            placeholder="${this.escapeAttribute(this.searchPlaceholder)}"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            maxlength="512"
            aria-label="Search"
            data-resource-search-input
          >
          <button class="ais-SearchBox-submit" type="submit" title="Submit the search query" hidden>
            <svg class="ais-SearchBox-submitIcon" width="10" height="10" viewBox="0 0 40 40" aria-hidden="true">
              <path d="M26.804 29.01c-2.832 2.34-6.465 3.746-10.426 3.746C7.333 32.756 0 25.424 0 16.378 0 7.333 7.333 0 16.378 0c9.046 0 16.378 7.333 16.378 16.378 0 3.96-1.406 7.594-3.746 10.426l10.534 10.534c.607.607.61 1.59-.004 2.202-.61.61-1.597.61-2.202.004L26.804 29.01zm-10.426.627c7.323 0 13.26-5.936 13.26-13.26 0-7.32-5.937-13.257-13.26-13.257C9.056 3.12 3.12 9.056 3.12 16.378c0 7.323 5.936 13.26 13.258 13.26z"></path>
            </svg>
          </button>
          <button class="ais-SearchBox-reset" type="reset" title="Clear the search query" hidden>
            <svg class="ais-SearchBox-resetIcon" viewBox="0 0 20 20" width="10" height="10" aria-hidden="true">
              <path d="M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z"></path>
            </svg>
          </button>
        </form>
      </div>
    `;
    this.searchInput?.addEventListener('input', this.handleSearchInput);
    this.searchForm?.addEventListener('reset', this.handleSearchReset);
    this.searchForm?.addEventListener('submit', this.handleSearchSubmit);
  }

  observeTabActivation() {
    this.tabPanel = this.closest('.yg-discover-design__panel');
    this.tabTitleContainer = document.querySelector(`yg-tabs-title[data-id="${this.dataset.tabsId}"]`);
    this.tabTitleContainer?.addEventListener('click', this.handleTabTitleClick);

    if (!this.tabPanel) {
      this.initialize();
      return;
    }

    this.tabActivationObserver = new MutationObserver(() => {
      if (this.tabPanel.classList.contains('is-active')) {
        this.initialize();
      }
    });
    this.tabActivationObserver.observe(this.tabPanel, { attributes: true, attributeFilter: ['class'] });

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (this.tabPanel?.classList.contains('is-active') || this.resourceType === 'documents') {
          this.initialize();
        }
      });
    });
  }

  handleTabTitleClick(event) {
    const tabTitle = event.target.closest('[data-resource-tab-key]');

    if (tabTitle?.dataset.resourceTabKey === this.dataset.resourceTabKey) {
      this.initialize();
    }
  }

  async getAllProductIds() {
    const cacheKey = [this.algoliaAppId, this.algoliaIndexName, this.algoliaSearchKey].join(':');
    let productIdsPromise = YGDiscoverDesignResources.productIdsPromises.get(cacheKey);

    if (!productIdsPromise) {
      productIdsPromise = this.fetchAllProductIds();
      YGDiscoverDesignResources.productIdsPromises.set(cacheKey, productIdsPromise);
      productIdsPromise.catch(() => YGDiscoverDesignResources.productIdsPromises.delete(cacheKey));
    }

    return productIdsPromise;
  }

  async fetchAllProductIds() {
    const algoliaSearch = this.getAlgoliaSearchFactory();

    if (!algoliaSearch) {
      throw new Error('Algolia search is not available.');
    }

    const searchClient = algoliaSearch(this.algoliaAppId, this.algoliaSearchKey);
    const seenProductIds = new Set();
    let page = 0;
    let totalHits = 0;
    let totalPages = 1;
    let retrievedHits = 0;

    while (page < totalPages) {
      const response = await searchClient.search([
        {
          indexName: this.algoliaIndexName,
          params: {
            query: '',
            page,
            hitsPerPage: this.algoliaScanPageSize,
            attributesToRetrieve: ['id']
          }
        }
      ]);
      const result = response?.results?.[0];

      if (!result) {
        throw new Error('Algolia did not return a search result.');
      }

      totalHits = Number(result.nbHits || 0);
      totalPages = Number(result.nbPages || 0);
      const hits = result.hits || [];
      retrievedHits += hits.length;

      this.getUniqueProductIds(hits).forEach((productId) => seenProductIds.add(productId));
      page += 1;
    }

    if (retrievedHits < totalHits) {
      throw new Error('The Algolia pagination limit prevented loading every product resource.');
    }

    return Array.from(seenProductIds);
  }

  async ensureProducts(productIds) {
    const missingIds = productIds.filter((productId) => !this.productsCache.has(this.getProductCacheKey(productId)));

    if (missingIds.length > 0) {
      const batches = this.chunkArray(missingIds, 20);
      const productBatches = await this.mapWithConcurrency(batches, 2, (batch) => this.fetchProducts(batch));

      productBatches.flat().forEach((product) => {
        this.productsCache.set(this.getProductCacheKey(product.numericId), product);
      });
    }

    return productIds
      .map((productId) => this.productsCache.get(this.getProductCacheKey(productId)))
      .filter(Boolean);
  }

  async mapWithConcurrency(items, limit, callback) {
    const results = new Array(items.length);
    let nextIndex = 0;
    const workerCount = Math.min(Math.max(limit, 1), items.length);

    await Promise.all(Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length) {
        const itemIndex = nextIndex;
        nextIndex += 1;
        results[itemIndex] = await callback(items[itemIndex], itemIndex);
      }
    }));

    return results;
  }

  async fetchProducts(productIds) {
    const ids = productIds.map((productId) => `gid://shopify/Product/${productId}`);
    const metafieldSelections = this.resourceMetafieldKeys.map((key, index) => `
              resourceMetafield${index}: metafield(namespace: "custom", key: "${key}") {
                value
                references(first: 50) {
                  nodes {
                    ... on Metaobject {
                      id
                      type
                      handle
                      fields {
                        key
                        value
                        reference {
                          __typename
                          ... on GenericFile {
                            url
                            mimeType
                          }
                          ... on MediaImage {
                            image {
                              url
                              altText
                            }
                          }
                          ... on Metaobject {
                            id
                            type
                            handle
                            fields {
                              key
                              value
                              reference {
                                __typename
                                ... on GenericFile {
                                  url
                                  mimeType
                                }
                              }
                            }
                          }
                        }
                        references(first: 20) {
                          nodes {
                            ... on Metaobject {
                              id
                              type
                              handle
                              fields {
                                key
                                value
                                reference {
                                  __typename
                                  ... on GenericFile {
                                    url
                                    mimeType
                                  }
                                  ... on MediaImage {
                                    image {
                                      url
                                      altText
                                    }
                                  }
                                }
                                references(first: 20) {
                                  nodes {
                                    ... on Metaobject {
                                      id
                                      type
                                      handle
                                      fields {
                                        key
                                        value
                                        reference {
                                          __typename
                                          ... on GenericFile {
                                            url
                                            mimeType
                                          }
                                          ... on MediaImage {
                                            image {
                                              url
                                              altText
                                            }
                                          }
                                        }
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }`).join('');
    const body = {
      query: `
        query getProducts($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              title
              handle
              productType
              featuredImage {
                url
                altText
              }
              ${metafieldSelections}
            }
          }
        }
      `,
      variables: { ids }
    };

    const response = await fetch(this.storefrontEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontToken
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed with status ${response.status}`);
    }

    const payload = await response.json();

    if (payload?.errors?.length) {
      throw new Error(payload.errors.map((error) => error.message).join('; '));
    }

    const nodes = payload?.data?.nodes || [];

    return nodes
      .filter(Boolean)
      .map((node) => this.normalizeProduct(node));
  }

  normalizeProduct(node) {
    const numericId = this.extractNumericId(node.id);
    const resources = [];
    this.resourceMetafieldKeys.forEach((_, index) => {
      const metaobjects = node?.[`resourceMetafield${index}`]?.references?.nodes || [];

      metaobjects.forEach((metaobject) => {
        this.getResourceMetaobjects(metaobject).forEach((resourceMetaobject) => {
          const fields = this.fieldsArrayToMap(resourceMetaobject.fields);
          resources.push(this.normalizeResource(fields, node));
        });
      });
    });

    return {
      id: node.id,
      numericId,
      title: node.title,
      handle: node.handle,
      productType: this.normalizeString(node.productType),
      featuredImageUrl: node?.featuredImage?.url || '',
      featuredImageAlt: node?.featuredImage?.altText || node.title,
      resources
    };
  }

  getResourceMetaobjects(metaobject) {
    const fields = this.fieldsArrayToMap(metaobject?.fields);
    const itemMetaobjects = fields.items?.references?.nodes || [];

    return itemMetaobjects.length > 0 ? itemMetaobjects : [metaobject];
  }

  normalizeResource(fields, product) {
    if (this.resourceType === 'images') {
      const image = this.getResourceImage(fields);
      const title = this.normalizeString(fields.title?.value) || product.title;
      const fileUrl = image?.url || '';
      const fileName = this.getImageFileName(fields, fileUrl, title);
      const fileType = this.getActualFileType({
        fileName,
        url: fileUrl,
        mimeType: this.normalizeString(image?.mimeType)
      });

      return {
        id: fields.uid?.value || title,
        title,
        subtitle: this.normalizeString(fields.sub_title?.value) || this.normalizeString(fields.subtitle?.value) || this.normalizeString(fields.image_type?.value),
        fileType,
        resourceType: this.normalizeString(fields.image_type?.value),
        fileSize: this.normalizeString(fields.file_size?.value),
        creationDate: this.normalizeString(fields.creation_date?.value),
        lastUpdated: this.normalizeString(fields.last_updated?.value),
        fileName,
        imageUrl: fileUrl || image?.url || product?.featuredImage?.url || '',
        imageAlt: image?.altText || product?.featuredImage?.altText || title,
        file: fileUrl
          ? {
              title,
              url: fileUrl,
              fileName
            }
          : null
      };
    }

    if (this.isModelResourceType()) {
      const title = this.normalizeString(fields.title?.value) || product.title;
      const files = this.parseDownloadFiles(fields.download_files, title, fields);
      const fileTypes = this.getUniqueValues(files.map((file) => file.fileType));
      const image = this.getResourceImage(fields);

      return {
        id: fields.uid?.value || title,
        title,
        subtitle: this.normalizeString(fields.subtitle?.value) || this.normalizeString(fields.brand?.value) || this.normalizeString(fields.product_name?.value) || product.title,
        fileType: fileTypes.join(', '),
        fileTypes,
        resourceType: this.normalizeString(fields.model_type?.value) || this.normalizeString(fields.resource_type?.value) || this.normalizeString(fields.category?.value),
        fileSize: this.normalizeString(fields.file_size?.value),
        creationDate: this.normalizeString(fields.creation_date?.value),
        lastUpdated: this.normalizeString(fields.last_updated?.value),
        imageUrl: image?.url || '',
        imageAlt: image?.altText || title,
        files
      };
    }

    const title = this.normalizeString(fields.title?.value) || product.title;
    const files = this.parseDownloadFiles(fields.download_files, title, fields);
    const fileType = files.length === 1 ? this.normalizeString(files[0]?.fileType) : '';
    const image = this.getResourceImage(fields);

    return {
      id: fields.uid?.value || title,
      title,
      subtitle: this.normalizeString(fields.subtitle?.value),
      fileType,
      resourceType: this.normalizeString(fields.document_type?.value),
      fileSize: this.normalizeString(fields.file_size?.value),
      creationDate: this.normalizeString(fields.creation_date?.value),
      lastUpdated: this.normalizeString(fields.last_updated?.value),
      imageUrl: image?.url || '',
      imageAlt: image?.altText || title,
      files
    };
  }

  getResourceImage(fields) {
    return fields.image?.reference?.image || fields.image_mobile?.reference?.image || null;
  }

  parseDownloadFiles(field, fallbackTitle, parentFields = {}) {
    const files = this.parseFlatDownloadFiles(parentFields, fallbackTitle);
    const nodes = field?.references?.nodes || [];

    const legacyFiles = nodes
        .filter((node) => node?.fields)
      .map((node) => {
        const fields = this.fieldsArrayToMap(node.fields);
        const fileField = fields.file_link || fields.file || fields.download_file || fields.file_url;
        const fileUrl = this.getFileUrl(fileField);

        if (!fileUrl) {
          return null;
        }

        const fileTitle = this.normalizeString(fields.title?.value) || fallbackTitle;
        const fileName = this.isModelResourceType()
          ? this.buildFileName(fileTitle, fileUrl, '')
          : this.getFileNameFromUrl(fileUrl);
        const actualFileType = this.getActualFileType({
          fileName,
          url: fileUrl,
          mimeType: this.normalizeString(fileField?.reference?.mimeType)
        });
        const fileType = this.isModelResourceType()
          ? this.normalizeString(fields.file_type?.value) || actualFileType
          : actualFileType;

        return {
          title: fileTitle,
          url: fileUrl,
          fileName,
          mimeType: this.normalizeString(fileField?.reference?.mimeType),
          fileSize: this.normalizeString(fields.file_size?.value),
          creationDate: this.normalizeString(fields.creation_date?.value),
          lastUpdated: this.normalizeString(fields.last_updated?.value),
          fileType
        };
      })
      .filter(Boolean);

    return files.length > 0 ? files : legacyFiles;
  }

  parseFlatDownloadFiles(fields, fallbackTitle) {
    return [1, 2, 3, 4]
      .map((index) => {
        const titleField = fields[`title_${index}`];
        const fileField = fields[`file_link_${index}`] || fields[`file_${index}`] || fields[`download_file_${index}`] || fields[`file_url_${index}`];
        const fileUrl = this.getFileUrl(fileField);

        if (!fileUrl) {
          return null;
        }

        const fileTitle = this.normalizeString(titleField?.value) || fallbackTitle;
        const fileName = this.isModelResourceType()
          ? this.buildFileName(fileTitle, fileUrl, '')
          : this.getFileNameFromUrl(fileUrl);
        const actualFileType = this.getActualFileType({
          fileName,
          url: fileUrl,
          mimeType: this.normalizeString(fileField?.reference?.mimeType)
        });
        const fileTypeField = fields[`file_type_${index}`];
        const fileType = this.isModelResourceType()
          ? this.normalizeString(fileTypeField?.value) || actualFileType
          : actualFileType;

        return {
          title: fileTitle,
          url: fileUrl,
          fileName,
          mimeType: this.normalizeString(fileField?.reference?.mimeType),
          fileSize: this.normalizeString(fields[`file_size_${index}`]?.value),
          creationDate: this.normalizeString(fields[`creation_date_${index}`]?.value),
          lastUpdated: this.normalizeString(fields[`last_updated_${index}`]?.value),
          fileType
        };
      })
      .filter(Boolean);
  }

  fieldsArrayToMap(fields) {
    return (fields || []).reduce((accumulator, field) => {
      accumulator[field.key] = field;
      return accumulator;
    }, {});
  }

  buildCards(products) {
    const cards = [];

    products.forEach((product) => {
      product.resources.forEach((resourceItem) => {
        if (this.resourceType === 'documents' && Array.isArray(resourceItem.files) && resourceItem.files.length > 0) {
          resourceItem.files.forEach((file) => {
            cards.push(this.buildCard(product, resourceItem, file));
          });
          return;
        }

        cards.push(this.buildCard(product, resourceItem, this.isModelResourceType() ? null : resourceItem.file));
      });
    });

    return cards;
  }

  buildCard(product, resourceItem, file) {
    const cardTitle = this.resourceType === 'documents' && resourceItem.files?.length > 1 && file?.title
      ? `${resourceItem.title || product.title} - ${file.title}`
      : resourceItem.title || product.title;
    const fileName = this.resourceType === 'documents'
      ? this.getFileNameFromUrl(file?.url)
      : this.isModelResourceType()
        ? ''
        : resourceItem.fileName || file?.fileName || this.getImageFileName({}, file?.url, cardTitle);
    const fileType = this.getActualFileType({
      fileName,
      url: file?.url,
      mimeType: file?.mimeType
    }) || file?.fileType || resourceItem.fileType || this.getFileTypeFromName(fileName || file?.url);
    const files = Array.isArray(resourceItem.files) ? resourceItem.files : [];
    const fileTypes = this.isModelResourceType() ? this.getUniqueValues(resourceItem.fileTypes || files.map((item) => item.fileType)) : [];
    const fileSize = file?.fileSize || resourceItem.fileSize || '';
    const creationDate = file?.creationDate || resourceItem.creationDate || '';
    const lastUpdated = file?.lastUpdated || resourceItem.lastUpdated || '';

    return {
      productId: product.numericId,
      productTitle: product.title,
      productType: this.normalizeString(product.productType),
      resourceType: this.normalizeString(resourceItem.resourceType),
      fileType: this.normalizeString(fileType),
      fileTypes,
      title: cardTitle,
      subtitle: resourceItem.subtitle || resourceItem.resourceType || product.productType,
      imageUrl: resourceItem.imageUrl || product.featuredImageUrl,
      imageAlt: resourceItem.imageAlt || product.featuredImageAlt || product.title,
      file: file ? { ...file, fileName } : file,
      files,
      fileMimeType: this.normalizeString(file?.mimeType),
      fileName,
      fileSize,
      creationDate,
      lastUpdated,
      lastUpdatedTime: this.parseDisplayDate(lastUpdated),
      searchableText: this.buildCardSearchText(product, resourceItem, file)
    };
  }

  buildCardSearchText(product, resourceItem, file) {
    return [
      product.title,
      product.productType,
      resourceItem.title,
      resourceItem.subtitle,
      resourceItem.resourceType,
      file?.fileType,
      resourceItem.fileType,
      ...(resourceItem.fileTypes || []),
      ...(Array.isArray(resourceItem.files) ? resourceItem.files.map((item) => item.fileType) : []),
      file?.title,
      file?.url
    ]
      .filter(Boolean)
      .join(' ');
  }

  getCardSearchText(card) {
    return [
      card.productTitle,
      card.productType,
      card.title,
      card.subtitle,
      card.resourceType,
      card.fileType,
      ...(card.fileTypes || []),
      card.fileName,
      card.file?.url
    ]
      .filter(Boolean)
      .join(' ');
  }

  // Facet attributes mapped to the per-card field that holds the value.
  get facetFieldMap() {
    return {
      fileType: 'fileType',
      resourceType: 'resourceType',
      productType: 'productType'
    };
  }

  // Keep only the cards matching the current facet selections (disjunctive within
  // a facet, conjunctive across facets) and re-render the results grid.
  applyFacets() {
    this.currentCards = this.searchFilteredCards.filter((card) => this.cardMatchesSelections(card, this.facetSelections));
    this.sortCards();
    this.renderCards();
  }

  sortCards() {
    if (this.sortMode === 'relevant') {
      return;
    }

    const direction = this.sortMode.endsWith('desc') ? -1 : 1;

    this.currentCards.sort((cardA, cardB) => {
      if (this.sortMode.startsWith('name')) {
        return direction * String(cardA.title || '').localeCompare(String(cardB.title || ''), undefined, { sensitivity: 'base' });
      }

      if (this.sortMode.startsWith('updated')) {
        return this.compareDateValues(cardA.lastUpdatedTime, cardB.lastUpdatedTime, direction);
      }

      return 0;
    });
  }

  applySearchFilter() {
    const queryWords = this.getSearchQueryWords();

    if (queryWords.length === 0) {
      this.searchFilteredCards = [...this.allCards];
      return;
    }

    this.searchFilteredCards = this.allCards.filter((card) => {
      const searchableText = this.normalizeSearchText(card.searchableText);

      return queryWords.every((word) => searchableText.includes(word));
    });
  }

  cardMatchesSelections(card, selections) {
    return Object.entries(this.facetFieldMap).every(([facetKey, field]) => {
      const selected = selections[facetKey];

      if (!selected || selected.size === 0) {
        return true;
      }

      return this.getFacetValues(card, facetKey, field).some((value) => selected.has(value));
    });
  }

  // Disjunctive facet counts: a facet's own selection is ignored when counting
  // its values, so each value reflects how many cards would remain if it were added.
  computeFacetCounts(facetKey) {
    const field = this.facetFieldMap[facetKey];
    const otherSelections = { ...this.facetSelections, [facetKey]: new Set() };
    const counts = new Map();

    this.searchFilteredCards.forEach((card) => {
      if (!this.cardMatchesSelections(card, otherSelections)) {
        return;
      }

      const values = this.getFacetValues(card, facetKey, field);

      if (values.length === 0) {
        return;
      }

      values.forEach((value) => {
        counts.set(value, (counts.get(value) || 0) + 1);
      });
    });

    return Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }

  getFacetValues(card, facetKey, field) {
    if (facetKey === 'fileType' && Array.isArray(card.fileTypes) && card.fileTypes.length > 0) {
      return this.getUniqueValues(card.fileTypes);
    }

    return this.getUniqueValues([card[field]]);
  }

  renderFacets() {
    this.renderFacetList(this.resourceTypeContainer, 'resourceType', 5);
    this.renderFacetList(this.fileTypeContainer, 'fileType', 5);
    this.renderProductTypeFacet();
  }

  renderFacetList(container, facetKey, limit) {
    if (!container) {
      return;
    }

    const items = this.computeFacetCounts(facetKey);

    if (items.length === 0) {
      container.innerHTML = '';
      return;
    }

    const selected = this.facetSelections[facetKey];
    const isShowingMore = this.facetShowMore[facetKey];
    const hasMore = items.length > limit;
    const visibleItems = isShowingMore ? items : items.slice(0, limit);

    const listMarkup = visibleItems.map((item) => {
      const isChecked = selected.has(item.value);

      return `
        <li class="ais-RefinementList-item${isChecked ? ' ais-RefinementList-item--selected' : ''}">
          <label class="ais-RefinementList-label">
            <input
              class="ais-RefinementList-checkbox"
              type="checkbox"
              value="${this.escapeAttribute(item.value)}"
              data-facet-checkbox="${facetKey}"
              ${isChecked ? 'checked' : ''}
            >
            <span class="ais-RefinementList-labelText">${this.escapeHtml(item.value)}</span>
            <span class="ais-RefinementList-count">${item.count}</span>
          </label>
        </li>
      `;
    }).join('');

    const showMoreMarkup = hasMore
      ? `<button type="button" class="ais-RefinementList-showMore" data-facet-show-more="${facetKey}">${this.escapeHtml(isShowingMore ? this.showLessText : this.showMoreText)}</button>`
      : '';

    container.innerHTML = `<ul class="ais-RefinementList-list">${listMarkup}</ul>${showMoreMarkup}`;
  }

  renderProductTypeFacet() {
    const container = this.productTypeContainer;

    if (!container) {
      return;
    }

    const items = this.computeFacetCounts('productType');

    if (items.length === 0) {
      container.innerHTML = '';
      return;
    }

    const groups = this.productTypeGroups;

    if (groups.length === 0) {
      this.renderFacetList(container, 'productType', 6);
      return;
    }

    const selected = this.facetSelections.productType;
    const itemMap = new Map(items.map((item) => [this.normalizeFacetValue(item.value), item]));
    const renderedValues = new Set();
    const groupMarkup = groups.map((group) => {
      const children = (group.children || [])
        .map((child) => itemMap.get(this.normalizeFacetValue(child)))
        .filter(Boolean);

      if (children.length === 0) {
        return '';
      }

      children.forEach((child) => renderedValues.add(this.normalizeFacetValue(child.value)));

      const childMarkup = children.map((item) => this.renderFacetCheckboxItem(item, 'productType', selected)).join('');

      return `
        <div class="yg-discover-design-resources__facet-group" data-product-type-group>
          <button type="button" class="yg-discover-design-resources__facet-group-toggle" data-product-type-group-toggle>
            <span>${this.escapeHtml(group.label)}</span>
            <span class="yg-discover-design-resources__facet-group-icon" aria-hidden="true">−</span>
          </button>
          <ul class="ais-RefinementList-list yg-discover-design-resources__facet-group-list">
            ${childMarkup}
          </ul>
        </div>
      `;
    }).join('');

    const ungroupedItems = items.filter((item) => !renderedValues.has(this.normalizeFacetValue(item.value)));
    const ungroupedMarkup = ungroupedItems.length > 0
      ? `<ul class="ais-RefinementList-list">${ungroupedItems.map((item) => this.renderFacetCheckboxItem(item, 'productType', selected)).join('')}</ul>`
      : '';

    container.innerHTML = `${groupMarkup}${ungroupedMarkup}`;
  }

  renderFacetCheckboxItem(item, facetKey, selected) {
    const isChecked = selected.has(item.value);

    return `
      <li class="ais-RefinementList-item${isChecked ? ' ais-RefinementList-item--selected' : ''}">
        <label class="ais-RefinementList-label">
          <input
            class="ais-RefinementList-checkbox"
            type="checkbox"
            value="${this.escapeAttribute(item.value)}"
            data-facet-checkbox="${facetKey}"
            ${isChecked ? 'checked' : ''}
          >
          <span class="ais-RefinementList-labelText">${this.escapeHtml(item.value)}</span>
          <span class="ais-RefinementList-count">${item.count}</span>
        </label>
      </li>
    `;
  }

  renderCards() {
    if (!this.cardsContainer) {
      return;
    }

    if (this.currentCards.length === 0) {
      this.currentPageCards = [];
      this.cardsContainer.innerHTML = `<div class="yg-discover-design-resources__empty text-base">${this.escapeHtml(this.noResultsText)}</div>`;
      this.renderPagination(0);
      this.updateResultsCount();
      this.updateSelectedCounter();
      return;
    }

    const totalPages = Math.max(Math.ceil(this.currentCards.length / this.pageSize), 1);
    const page = Math.min(this.currentPage, totalPages);
    const startIndex = (page - 1) * this.pageSize;
    const pageCards = this.currentCards.slice(startIndex, startIndex + this.pageSize);

    this.currentPage = page;
    this.currentPageCards = pageCards;
    this.cardsContainer.innerHTML = pageCards.map((card, index) => this.renderCard(card, startIndex + index)).join('');
    this.renderPagination(totalPages);
    this.updateResultsCount();
    this.syncRenderedSelections();
    this.updateSelectedCounter();
  }

  renderCard(card, cardIndex = 0) {
    if (this.isModelResourceType()) {
      return this.renderModelCard(card, cardIndex);
    }

    const imageMarkup = card.imageUrl
      ? `<img src="${this.escapeAttribute(card.imageUrl)}" alt="${this.escapeAttribute(card.imageAlt || card.title)}" class="yg-discover-design-resources__card-image">`
      : `<div class="yg-discover-design-resources__card-placeholder"></div>`;

    const downloadMarkup = this.renderSingleFileButton(card.file, card.title);
    const pageCardIndex = this.currentPageCards.indexOf(card);
    const previewMarkup = this.renderPreviewButton(card, pageCardIndex);
    const metaLabel = this.resourceType === 'images' ? 'Image Type' : 'Document Type';
    const cardKey = this.getCardKey(card, cardIndex);
    const iconMarkup = this.resourceType === 'documents' && this.isPdfFile(card.file)
      ? this.renderPdfFileIcon()
      : '';

    return `
      <article class="yg-discover-design-resources__card yg-discover-design-resources__card--single" data-card-id="${this.escapeAttribute(cardKey)}">
        <div class="yg-discover-design-resources__card-media">
          ${imageMarkup}
          ${this.renderCardCheckbox(card, cardKey)}
          ${previewMarkup}
          ${downloadMarkup}
        </div>
        <div class="yg-discover-design-resources__card-body">
          <div class="yg-discover-design-resources__card-header${iconMarkup ? ' yg-discover-design-resources__card-header--with-icon' : ''}">
            ${iconMarkup}
            <div class="yg-discover-design-resources__card-copy">
              <h3 class="yg-discover-design-resources__card-title h4">${this.escapeHtml(card.title)}</h3>
              <p class="yg-discover-design-resources__card-subtitle text-sm">${this.escapeHtml(card.subtitle || '')}</p>
            </div>
          </div>
        </div>
        <div class="yg-discover-design-resources__card-metadata">
          ${this.renderMetadataRow(metaLabel, card.resourceType)}
          ${this.renderMetadataRow('File Type', card.fileType)}
          ${this.renderMetadataRow('File Size', card.fileSize)}
          ${this.renderMetadataRow('File Name', card.fileName, 'yg-discover-design-resources__metadata-value--file-name')}
          ${this.renderMetadataRow('Creation Date', card.creationDate)}
          ${this.renderMetadataRow('Last Updated', card.lastUpdated)}
        </div>
      </article>
    `;
  }

  renderModelCard(card, cardIndex = 0) {
    const imageMarkup = card.imageUrl
      ? `<img src="${this.escapeAttribute(card.imageUrl)}" alt="${this.escapeAttribute(card.imageAlt || card.title)}" class="yg-discover-design-resources__card-image">`
      : `<div class="yg-discover-design-resources__card-placeholder"></div>`;
    const cardKey = this.getCardKey(card, cardIndex);

    return `
      <article class="yg-discover-design-resources__card yg-discover-design-resources__card--model" data-card-id="${this.escapeAttribute(cardKey)}">
        <div class="yg-discover-design-resources__model-media">
          ${imageMarkup}
          ${this.renderCardCheckbox(card, cardKey)}
        </div>
        ${this.renderModelDownloadButton(card)}
        <div class="yg-discover-design-resources__model-body">
          <h3 class="yg-discover-design-resources__model-title h4">${this.escapeHtml(card.title)}</h3>
          <p class="yg-discover-design-resources__model-subtitle text-sm">${this.escapeHtml(card.subtitle || '')}</p>
        </div>
      </article>
    `;
  }

  renderPreviewButton(card, cardIndex = -1) {
    if (!card.file?.url || (this.resourceType !== 'documents' && this.resourceType !== 'images')) {
      return '';
    }

    return `
      <button
        type="button"
        class="yg-discover-design-resources__preview-button text-sm"
        data-preview-trigger
        data-preview-url="${this.escapeAttribute(card.file.url)}"
        data-preview-title="${this.escapeAttribute(card.title)}"
        data-preview-file-name="${this.escapeAttribute(card.fileName || card.file.fileName || this.buildFileName(card.title, card.file.url))}"
        data-preview-file-type="${this.escapeAttribute(card.fileType || '')}"
        data-preview-mime-type="${this.escapeAttribute(card.file?.mimeType || '')}"
        data-preview-card-index="${this.escapeAttribute(cardIndex)}"
        aria-label="${this.escapeAttribute(`${this.previewText} ${card.title}`)}"
      >
        <span>${this.escapeHtml(this.previewText)}</span>
      </button>
    `;
  }

  renderPdfFileIcon() {
    return `
      <span class="yg-discover-design-resources__card-file-icon" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M6.66668 29.3334H25.3333C26.0697 29.3334 26.6667 28.7365 26.6667 28.0001V9.33341H20V2.66675H6.66668C5.9303 2.66675 5.33334 3.2637 5.33334 4.00008V28.0001C5.33334 28.7365 5.9303 29.3334 6.66668 29.3334Z" fill="#001D34" stroke="#001D34" stroke-linecap="round" stroke-linejoin="round"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M12 12H20V17.3278L12.0055 17.3333L12 12Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 12V22.6667" stroke="white" stroke-linecap="round"/>
          <path d="M20 2.66675L26.6667 9.33341" stroke="#001D34" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    `;
  }

  renderCardCheckbox(card, cardKey) {
    const files = this.getCardDownloadFiles(card);

    if (files.length === 0) {
      return '';
    }

    return `
      <label class="yg-discover-design-resources__card-checkbox" aria-label="Select ${this.escapeAttribute(card.title)}">
        <input
          type="checkbox"
          class="yg-discover-design-resources__card-checkbox-input"
          data-card-checkbox
          data-file-key="${this.escapeAttribute(cardKey)}"
          data-file-url="${this.escapeAttribute(files[0]?.url || '')}"
          data-file-size="${this.escapeAttribute(files.reduce((total, file) => total + this.getFileSizeBytes(file.fileSize), 0))}"
          data-product-title="${this.escapeAttribute(card.title)}"
          data-file-name="${this.escapeAttribute(files[0]?.fileName || this.buildFileName(card.title, files[0]?.url || ''))}"
          data-card-files="${this.escapeAttribute(JSON.stringify(files.map((file) => ({
            url: file.url,
            size: this.getFileSizeBytes(file.fileSize),
            title: file.title || card.title,
            fileName: file.fileName || this.buildFileName(file.title || card.title, file.url)
          }))))}"
        >
        <span class="yg-discover-design-resources__card-checkbox-custom"></span>
      </label>
    `;
  }

  renderMetadataRow(label, value, valueClass = '') {
    return `
      <div class="yg-discover-design-resources__metadata-row">
        <p class="yg-discover-design-resources__metadata-label">${this.escapeHtml(label)}</p>
        <p class="yg-discover-design-resources__metadata-value ${valueClass}">${this.escapeHtml(value || '')}</p>
      </div>
    `;
  }

  renderSingleFileButton(file, fallbackTitle) {
    if (!file?.url) {
      return `
        <span class="yg-discover-design-resources__download-button yg-discover-design-resources__download-button--disabled text-sm">
          <span>${this.escapeHtml(this.downloadText)}</span>
        </span>
      `;
    }

    return `
      <a
        class="yg-discover-design-resources__download-button yg-discover-design-resources__download-button--overlay text-sm"
        href="${this.escapeAttribute(file.url)}"
        download="${this.escapeAttribute(file.fileName || this.buildFileName(file.title || fallbackTitle, file.url))}"
        rel="noopener"
        aria-label="${this.escapeAttribute(`${this.downloadText} ${fallbackTitle}`)}"
      >
        <svg class="yg-discover-design-resources__download-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M8 2.5V10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
          <path d="M5 7.5L8 10.5L11 7.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M3.5 13H12.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
        </svg>
      </a>
    `;
  }

  renderPagination(totalPages) {
    if (!this.paginationContainer) {
      return;
    }

    if (totalPages <= 1) {
      this.paginationContainer.hidden = true;
      this.paginationContainer.innerHTML = '';
      return;
    }

    this.paginationContainer.hidden = false;
    this.paginationContainer.className = 'yg-discover-design-resources__pagination collection__pagination pagination';
    const pages = this.getVisiblePages(totalPages);

    this.paginationContainer.innerHTML = `
      ${this.currentPage <= 1
    ? '<span class="pagination__item pagination__item--edge pagination__item--disabled"><span class="pagination__icon pagination__icon--first" aria-hidden="true">&laquo;</span></span>'
    : '<button type="button" class="pagination__item pagination__item--edge group" data-pagination-first aria-label="First page"><span class="pagination__icon pagination__icon--first" aria-hidden="true">&laquo;</span></button>'}
      ${this.currentPage <= 1
    ? '<span class="pagination__item pagination__item--disabled"><span class="pagination__icon" aria-hidden="true">&lsaquo;</span></span>'
    : `<button type="button" class="pagination__item group" data-pagination-prev aria-label="${this.escapeAttribute(this.previousText)}"><span class="pagination__icon" aria-hidden="true">&lsaquo;</span></button>`}
      ${pages.map((page) => `
        ${page === this.currentPage
    ? `<span class="pagination__item pagination__page pagination__item--active" aria-current="page" data-pagination-distance="${Math.abs(this.currentPage - page)}">${page}</span>`
    : `<button type="button" class="pagination__item pagination__page group" data-pagination-page="${page}" data-pagination-distance="${Math.abs(this.currentPage - page)}" aria-label="Go to page ${page}">${page}</button>`}
      `).join('')}
      ${this.currentPage >= totalPages
    ? '<span class="pagination__item pagination__item--disabled"><span class="pagination__icon" aria-hidden="true">&rsaquo;</span></span>'
    : `<button type="button" class="pagination__item group" data-pagination-next aria-label="${this.escapeAttribute(this.nextText)}"><span class="pagination__icon" aria-hidden="true">&rsaquo;</span></button>`}
      ${this.currentPage >= totalPages
    ? '<span class="pagination__item pagination__item--edge pagination__item--disabled"><span class="pagination__icon pagination__icon--last" aria-hidden="true">&raquo;</span></span>'
    : '<button type="button" class="pagination__item pagination__item--edge group" data-pagination-last aria-label="Last page"><span class="pagination__icon pagination__icon--last" aria-hidden="true">&raquo;</span></button>'}
    `;
  }

  getVisiblePages(totalPages) {
    const start = Math.max(this.currentPage - 2, 1);
    const end = Math.min(this.currentPage + 2, totalPages);
    const pages = [];

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    return pages;
  }

  renderLoadingState() {
    if (!this.cardsContainer) {
      return;
    }

    this.cardsContainer.innerHTML = `<div class="yg-discover-design-resources__loading text-base">${this.escapeHtml(this.loadingText)}</div>`;
    if (this.paginationContainer) {
      this.paginationContainer.hidden = true;
      this.paginationContainer.innerHTML = '';
    }
    this.updateResultsCount(0);
  }

  renderErrorState() {
    if (!this.cardsContainer) {
      return;
    }

    this.cardsContainer.innerHTML = `<div class="yg-discover-design-resources__empty text-base">${this.escapeHtml(this.errorText)}</div>`;
    if (this.paginationContainer) {
      this.paginationContainer.hidden = true;
      this.paginationContainer.innerHTML = '';
    }
    this.updateResultsCount(0);
  }

  handleClick(event) {
    if (!event.target.closest('[data-sort-dropdown]')) {
      this.closeSortDropdowns();
    }

    if (event.target === this && this.isMobileLayout() && this.filtersOpen) {
      this.setFiltersOpen(false);
      return;
    }

    const filtersClose = event.target.closest('[data-filters-close]');

    if (filtersClose && this.contains(filtersClose)) {
      event.preventDefault();
      this.setFiltersOpen(false);
      return;
    }

    const filtersApply = event.target.closest('[data-filters-apply]');

    if (filtersApply && this.contains(filtersApply)) {
      event.preventDefault();
      this.setFiltersOpen(false);
      this.scrollResultsIntoView();
      return;
    }

    const filterToggle = event.target.closest('[data-filters-toggle]');

    if (filterToggle && this.contains(filterToggle)) {
      event.preventDefault();
      this.setFiltersOpen(!this.filtersOpen);
      return;
    }

    const viewTrigger = event.target.closest('[data-view-trigger]');

    if (viewTrigger && this.contains(viewTrigger)) {
      event.preventDefault();
      this.setViewMode(viewTrigger.dataset.viewTrigger || 'grid');
      return;
    }

    const scaleMinus = event.target.closest('[data-grid-scale-minus]');

    if (scaleMinus && this.contains(scaleMinus)) {
      event.preventDefault();
      this.setGridScale(this.gridScale - 1);
      return;
    }

    const scalePlus = event.target.closest('[data-grid-scale-plus]');

    if (scalePlus && this.contains(scalePlus)) {
      event.preventDefault();
      this.setGridScale(this.gridScale + 1);
      return;
    }

    const facetToggle = event.target.closest('[data-facet-toggle]');

    if (facetToggle && this.contains(facetToggle)) {
      event.preventDefault();
      const section = facetToggle.closest('[data-facet-section]');

      if (!section) {
        return;
      }

      const isCollapsed = section.dataset.collapsed === 'true';
      section.dataset.collapsed = isCollapsed ? 'false' : 'true';
      return;
    }

    const showMoreButton = event.target.closest('[data-facet-show-more]');

    if (showMoreButton && this.contains(showMoreButton)) {
      event.preventDefault();
      const facetKey = showMoreButton.dataset.facetShowMore;

      if (facetKey in this.facetShowMore) {
        this.facetShowMore[facetKey] = !this.facetShowMore[facetKey];
        this.renderFacets();
      }
      return;
    }

    const dropdownToggle = event.target.closest('[data-download-toggle]');

    if (dropdownToggle && this.contains(dropdownToggle)) {
      event.preventDefault();
      this.toggleDropdown(dropdownToggle);
      return;
    }

    const firstButton = event.target.closest('[data-pagination-first]');

    if (firstButton && this.contains(firstButton)) {
      event.preventDefault();
      if (this.currentPage > 1) {
        this.clearSelections();
        this.currentPage = 1;
        this.renderCards();
        this.scrollResultsIntoView();
      }
      return;
    }

    const prevButton = event.target.closest('[data-pagination-prev]');

    if (prevButton && this.contains(prevButton)) {
      event.preventDefault();
      if (this.currentPage > 1) {
        this.clearSelections();
        this.currentPage -= 1;
        this.renderCards();
        this.scrollResultsIntoView();
      }
      return;
    }

    const nextButton = event.target.closest('[data-pagination-next]');

    if (nextButton && this.contains(nextButton)) {
      event.preventDefault();
      const totalPages = Math.max(Math.ceil(this.currentCards.length / this.pageSize), 1);

      if (this.currentPage < totalPages) {
        this.clearSelections();
        this.currentPage += 1;
        this.renderCards();
        this.scrollResultsIntoView();
      }
      return;
    }

    const lastButton = event.target.closest('[data-pagination-last]');

    if (lastButton && this.contains(lastButton)) {
      event.preventDefault();
      const totalPages = Math.max(Math.ceil(this.currentCards.length / this.pageSize), 1);

      if (this.currentPage < totalPages) {
        this.clearSelections();
        this.currentPage = totalPages;
        this.renderCards();
        this.scrollResultsIntoView();
      }
      return;
    }

    const pageButton = event.target.closest('[data-pagination-page]');

    if (pageButton && this.contains(pageButton)) {
      event.preventDefault();
      const page = Number.parseInt(pageButton.dataset.paginationPage || '1', 10);

      if (!Number.isNaN(page) && page !== this.currentPage) {
        this.clearSelections();
        this.currentPage = page;
        this.renderCards();
        this.scrollResultsIntoView();
      }
      return;
    }

    const downloadLink = event.target.closest('.yg-discover-design-resources__download-button[download], .yg-discover-design-resources__download-item[download], .yg-discover-design-resources__model-download[download]');

    if (downloadLink && this.contains(downloadLink)) {
      this.handleDownloadLink(event, downloadLink);
      return;
    }

    const previewTrigger = event.target.closest('[data-preview-trigger]');

    if (previewTrigger && this.contains(previewTrigger)) {
      event.preventDefault();
      this.openPreviewFromTrigger(previewTrigger);
      return;
    }

    const previewThumbnail = event.target.closest('[data-preview-thumbnail]');

    if (previewThumbnail && this.contains(previewThumbnail)) {
      event.preventDefault();
      const pageNumber = Number.parseInt(previewThumbnail.dataset.previewThumbnail || '1', 10);

      if (!Number.isNaN(pageNumber)) {
        this.scrollToPdfPage(pageNumber);
      }
      return;
    }

    const imagePreviewNav = event.target.closest('[data-image-preview-nav]');

    if (imagePreviewNav && this.contains(imagePreviewNav)) {
      event.preventDefault();
      this.navigateImagePreview(imagePreviewNav.dataset.imagePreviewNav === 'next' ? 1 : -1);
      return;
    }

    const previewCloseTrigger = event.target.closest('[data-preview-close]');

    if (previewCloseTrigger && this.contains(previewCloseTrigger)) {
      event.preventDefault();
      this.closePreviewModal();
      return;
    }

    if (event.target === this.previewModal) {
      event.preventDefault();
      this.closePreviewModal();
      return;
    }

    const productTypeGroupToggle = event.target.closest('[data-product-type-group-toggle]');

    if (productTypeGroupToggle && this.contains(productTypeGroupToggle)) {
      event.preventDefault();
      const group = productTypeGroupToggle.closest('[data-product-type-group]');
      const isCollapsed = group?.dataset.collapsed === 'true';

      if (group) {
        group.dataset.collapsed = isCollapsed ? 'false' : 'true';
      }

      return;
    }

    const sortToggle = event.target.closest('[data-sort-toggle]');

    if (sortToggle && this.contains(sortToggle)) {
      event.preventDefault();
      this.toggleSortDropdown(sortToggle);
      return;
    }

    const sortOption = event.target.closest('[data-sort-option]');

    if (sortOption && this.contains(sortOption)) {
      event.preventDefault();
      this.setSortMode(sortOption.dataset.sortOption || 'relevant', sortOption.textContent.trim());
      return;
    }

    const floatCloseButton = event.target.closest('[data-float-close-trigger]');

    if (floatCloseButton && this.contains(floatCloseButton)) {
      event.preventDefault();
      this.exitMultiSelectMode();
      return;
    }

    const floatDownloadButton = event.target.closest('[data-float-download-trigger]');

    if (floatDownloadButton && this.contains(floatDownloadButton)) {
      event.preventDefault();
      this.startBatchDownload();
    }
  }

  handleDocumentClick(event) {
    if (this.contains(event.target)) {
      return;
    }

    this.closeDropdowns();
    this.closeSortDropdowns();
  }

  renderModelDownloadButton(card) {
    const files = this.getCardDownloadFiles(card);
    const downloadLabel = this.getModelDownloadLabel();

    if (files.length === 0) {
      return `
        <span class="yg-discover-design-resources__model-download yg-discover-design-resources__model-download--disabled text-sm">
          <span>${this.escapeHtml(downloadLabel)}</span>
        </span>
      `;
    }

    if (files.length === 1) {
      const file = files[0];

      return `
        <a
          class="yg-discover-design-resources__model-download text-sm"
          href="${this.escapeAttribute(file.url)}"
          download="${this.escapeAttribute(file.fileName || this.buildFileName(file.title || card.title, file.url))}"
          rel="noopener"
          aria-label="${this.escapeAttribute(`${downloadLabel} ${card.title}`)}"
        >
          <span>${this.escapeHtml(downloadLabel)}</span>
          ${this.renderChevronIcon()}
        </a>
      `;
    }

    return `
      <div class="yg-discover-design-resources__download-dropdown yg-discover-design-resources__model-download-dropdown" data-download-dropdown>
        <button
          class="yg-discover-design-resources__model-download text-sm"
          type="button"
          data-download-toggle
          aria-expanded="false"
        >
          <span>${this.escapeHtml(downloadLabel)}</span>
          ${this.renderChevronIcon()}
        </button>
        <div class="yg-discover-design-resources__download-menu yg-discover-design-resources__model-download-menu" data-download-menu hidden>
          ${files.map((file) => `
            <a
              class="yg-discover-design-resources__download-item text-sm"
              href="${this.escapeAttribute(file.url)}"
              download="${this.escapeAttribute(file.fileName || this.buildFileName(file.title || card.title, file.url))}"
              rel="noopener"
            >
              <span class="yg-discover-design-resources__download-item-label">
                ${this.renderDownloadItemIcon()}
                <span>${this.escapeHtml(file.title || file.fileType || file.fileName || this.downloadText)}</span>
              </span>
              ${file.fileSize ? `<span class="yg-discover-design-resources__download-item-size">${this.escapeHtml(file.fileSize)}</span>` : ''}
            </a>
          `).join('')}
        </div>
      </div>
    `;
  }

  getModelDownloadLabel() {
    if (this.resourceType === 'autocad') {
      return `${this.downloadText} (CAD)`;
    }

    if (this.resourceType === 'revit') {
      return `${this.downloadText} (Revit)`;
    }

    return this.downloadText;
  }

  renderDownloadItemIcon() {
    return `
      <svg class="yg-discover-design-resources__download-item-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M8 2.5V10" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>
        <path d="M5 7.5L8 10.5L11 7.5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M3.5 13H12.5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>
      </svg>
    `;
  }

  renderChevronIcon() {
    return `
      <svg class="yg-discover-design-resources__button-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  handleDocumentKeydown(event) {
    if (event.key === 'Escape' && this.isPreviewOpen()) {
      this.closePreviewModal();
      return;
    }

    if (!this.isPreviewOpen() || this.previewModal?.dataset.previewType !== 'image-gallery') {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigateImagePreview(-1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigateImagePreview(1);
    }
  }

  handlePdfPreviewScroll() {
    if (!this.isPreviewOpen() || !this.currentPdfDocument || !this.pdfPreviewMain) {
      return;
    }

    const pages = Array.from(this.pdfPreviewMain.querySelectorAll('[data-preview-page]'));

    if (pages.length === 0) {
      return;
    }

    const containerTop = this.pdfPreviewMain.scrollTop;
    let activePage = 1;
    let smallestOffset = Number.POSITIVE_INFINITY;

    pages.forEach((page) => {
      const pageNumber = Number.parseInt(page.dataset.previewPage || '1', 10);
      const offset = Math.abs(page.offsetTop - containerTop);

      if (offset < smallestOffset) {
        smallestOffset = offset;
        activePage = pageNumber;
      }
    });

    this.setActivePreviewPage(activePage);
  }

  handleChange(event) {
    const scaleSlider = event.target.closest('[data-grid-scale-slider]');

    if (scaleSlider && this.contains(scaleSlider)) {
      this.setGridScale(scaleSlider.value);
      return;
    }

    const multiSelectToggle = event.target.closest('[data-multi-select-trigger]');

    if (multiSelectToggle && this.contains(multiSelectToggle)) {
      if (this.isMobileLayout()) {
        multiSelectToggle.checked = false;
        this.toggleMultiSelectMode(false);
        return;
      }

      this.toggleMultiSelectMode(multiSelectToggle.checked);
      return;
    }

    const cardCheckbox = event.target.closest('[data-card-checkbox]');

    if (cardCheckbox && this.contains(cardCheckbox)) {
      if (this.isMobileLayout()) {
        cardCheckbox.checked = false;
        return;
      }

      this.handleCardCheckboxChange(cardCheckbox);
      return;
    }

    const floatSelectAll = event.target.closest('[data-float-select-all-trigger]');

    if (floatSelectAll && this.contains(floatSelectAll)) {
      this.selectAllCards(floatSelectAll.checked);
      return;
    }

    const checkbox = event.target.closest('[data-facet-checkbox]');

    if (!checkbox || !this.contains(checkbox)) {
      return;
    }

    const facetKey = checkbox.dataset.facetCheckbox;
    const selected = this.facetSelections[facetKey];

    if (!selected) {
      return;
    }

    if (checkbox.checked) {
      selected.add(checkbox.value);
    } else {
      selected.delete(checkbox.value);
    }

    this.currentPage = 1;
    this.applyFacets();
    this.renderFacets();
  }

  handleSearchInput() {
    this.syncSearchResetButton();
    const querySignature = this.getQuerySignature();

    if (querySignature === this.lastQuerySignature) {
      return;
    }

    this.lastQuerySignature = querySignature;
    this.currentPage = 1;
    this.clearSelections();
    this.applySearchFilter();
    this.applyFacets();
    this.renderFacets();
  }

  handleSearchReset(event) {
    event.preventDefault();

    if (!this.searchInput) {
      return;
    }

    this.searchInput.value = '';
    this.handleSearchInput();
  }

  handleSearchSubmit(event) {
    event.preventDefault();
  }

  syncSearchResetButton() {
    if (this.searchResetButton) {
      this.searchResetButton.hidden = this.searchInput?.value.trim() === '';
    }
  }

  setFiltersOpen(open) {
    this.filtersOpen = Boolean(open);
    this.dataset.filtersOpen = this.filtersOpen ? 'true' : 'false';

    this.syncFiltersToggle();
    this.setGridScale(this.gridScale);
  }

  isMobileLayout() {
    return window.matchMedia('(max-width: 999px)').matches;
  }

  syncFiltersToggle() {
    const label = this.filtersOpen ? this.hideFiltersText : this.showFiltersText;

    this.querySelectorAll('[data-filters-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', this.filtersOpen ? 'true' : 'false');
    });

    this.querySelectorAll('[data-filters-toggle-label]').forEach((buttonLabel) => {
      buttonLabel.textContent = label;
    });
  }

  setViewMode(view, render = true) {
    this.viewMode = this.isModelResourceType() ? 'grid' : view === 'list' ? 'list' : 'grid';
    this.dataset.viewMode = this.viewMode;

    this.querySelectorAll('[data-view-trigger]').forEach((button) => {
      button.classList.toggle('is-active', button.dataset.viewTrigger === this.viewMode);
    });

    if (render) {
      this.renderCards();
    }
  }

  setGridScale(scale) {
    const min = Number.parseInt(this.gridScaleSlider?.min || '2', 10);
    const max = Number.parseInt(this.gridScaleSlider?.max || '4', 10);
    const normalizedScale = Math.min(Math.max(Number.parseInt(scale, 10) || 3, min), max);
    const effectiveScale = this.getEffectiveGridScale(normalizedScale);

    this.gridScale = normalizedScale;
    this.dataset.gridScale = String(normalizedScale);

    if (this.gridScaleSlider) {
      this.gridScaleSlider.value = String(normalizedScale);
    }

    if (this.cardsContainer) {
      this.cardsContainer.style.setProperty('--yg-discover-design-grid-scale', effectiveScale);
    }
  }

  getEffectiveGridScale(scale) {
    if (this.isMobileLayout()) {
      return 2;
    }

    let effectiveScale = Number.parseInt(scale, 10) || 3;

    if (window.innerWidth >= 1000 && !this.filtersOpen) {
      effectiveScale += 1;
    }

    return Math.min(effectiveScale, 5);
  }

  toggleMultiSelectMode(enabled) {
    const shouldEnable = Boolean(enabled) && !this.isMobileLayout();

    this.dataset.multiSelect = shouldEnable ? 'true' : 'false';

    if (shouldEnable) {
      document.body.classList.add('yg-multi-select-active');
    } else {
      document.body.classList.remove('yg-multi-select-active');
      this.clearSelections();
    }

    if (this.floatPanel) {
      if (shouldEnable) {
        this.floatPanel.removeAttribute('hidden');
      } else {
        this.floatPanel.setAttribute('hidden', '');
      }
    }
  }

  exitMultiSelectMode() {
    if (this.multiSelectToggle) {
      this.multiSelectToggle.checked = false;
    }

    this.toggleMultiSelectMode(false);
  }

  handleCardCheckboxChange(checkbox) {
    const fileKey = checkbox.dataset.fileKey || this.getFileKey(checkbox.dataset.fileUrl, checkbox.dataset.fileName);
    const card = checkbox.closest('.yg-discover-design-resources__card');

    if (checkbox.checked) {
      this.selectedFiles.set(fileKey, {
        url: checkbox.dataset.fileUrl,
        size: Number.parseInt(checkbox.dataset.fileSize || '0', 10) || 0,
        title: checkbox.dataset.productTitle || 'File',
        fileName: checkbox.dataset.fileName || this.getDownloadNameFromUrl(checkbox.dataset.fileUrl),
        files: this.parseSelectedCardFiles(checkbox)
      });
      card?.classList.add('is-selected');
    } else {
      this.selectedFiles.delete(fileKey);
      card?.classList.remove('is-selected');

      if (this.floatSelectAll) {
        this.floatSelectAll.checked = false;
      }
    }

    this.updateSelectedCounter();
  }

  selectAllCards(checked) {
    if (checked) {
      this.querySelectorAll('[data-card-checkbox]').forEach((checkbox) => {
        if (!checkbox.checked) {
          checkbox.checked = true;
        }

        this.handleCardCheckboxChange(checkbox);
      });

      return;
    }

    this.querySelectorAll('[data-card-checkbox]').forEach((checkbox) => {
      if (checkbox.checked) {
        checkbox.checked = false;
      }

      this.handleCardCheckboxChange(checkbox);
    });
  }

  clearSelections() {
    this.querySelectorAll('[data-card-checkbox]').forEach((checkbox) => {
      checkbox.checked = false;
      checkbox.closest('.yg-discover-design-resources__card')?.classList.remove('is-selected');
    });

    this.selectedFiles.clear();

    if (this.floatSelectAll) {
      this.floatSelectAll.checked = false;
    }

    this.updateSelectedCounter();
  }

  syncRenderedSelections() {
    this.querySelectorAll('[data-card-checkbox]').forEach((checkbox) => {
      const fileKey = checkbox.dataset.fileKey || this.getFileKey(checkbox.dataset.fileUrl, checkbox.dataset.fileName);
      const checked = this.selectedFiles.has(fileKey);

      checkbox.checked = checked;
      checkbox.closest('.yg-discover-design-resources__card')?.classList.toggle('is-selected', checked);
    });
  }

  updateSelectedCounter() {
    const count = this.selectedFiles.size;
    const currentPageCheckboxes = Array.from(this.querySelectorAll('[data-card-checkbox]'));
    const selectableCount = currentPageCheckboxes.length;

    this.querySelectorAll('[data-selected-count]').forEach((counter) => {
      counter.textContent = count;
    });

    if (this.floatSelectAll) {
      const selectedCurrentPageCount = currentPageCheckboxes.filter((checkbox) => checkbox.checked).length;

      this.floatSelectAll.checked = selectableCount > 0 && selectedCurrentPageCount >= selectableCount;
    }

    if (this.floatDownloadButton) {
      this.floatDownloadButton.disabled = count === 0;
    }
  }

  toggleSortDropdown(toggle) {
    const dropdown = toggle.closest('[data-sort-dropdown]');
    const menu = dropdown?.querySelector('[data-sort-menu]');

    if (!dropdown || !menu) {
      return;
    }

    const isOpen = !menu.hidden;
    this.closeSortDropdowns(dropdown);
    dropdown.dataset.open = isOpen ? 'false' : 'true';
    toggle.setAttribute('aria-expanded', String(!isOpen));
    menu.hidden = isOpen;
  }

  closeSortDropdowns(exception = null) {
    this.querySelectorAll('[data-sort-dropdown]').forEach((dropdown) => {
      if (exception && dropdown === exception) {
        return;
      }

      const menu = dropdown.querySelector('[data-sort-menu]');
      const toggle = dropdown.querySelector('[data-sort-toggle]');

      dropdown.dataset.open = 'false';

      if (menu) {
        menu.hidden = true;
      }

      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  setSortMode(value, label) {
    this.sortMode = value || 'relevant';
    this.currentPage = 1;
    this.clearSelections();

    this.querySelectorAll('[data-sort-option]').forEach((option) => {
      option.classList.toggle('is-active', option.dataset.sortOption === this.sortMode);
    });

    this.querySelectorAll('[data-sort-label]').forEach((labelElement) => {
      labelElement.textContent = label || 'Most Relevant';
    });

    this.closeSortDropdowns();
    this.applyFacets();
  }

  toggleDropdown(toggle) {
    const dropdown = toggle.closest('[data-download-dropdown]');
    const menu = dropdown?.querySelector('[data-download-menu]');

    if (!dropdown || !menu) {
      return;
    }

    const isOpen = !menu.hidden;
    this.closeDropdowns(dropdown);
    dropdown.classList.toggle('is-open', !isOpen);
    toggle.setAttribute('aria-expanded', String(!isOpen));
    menu.hidden = isOpen;
  }

  closeDropdowns(exception = null) {
    this.querySelectorAll('[data-download-dropdown]').forEach((dropdown) => {
      if (exception && dropdown === exception) {
        return;
      }

      dropdown.classList.remove('is-open');
      const menu = dropdown.querySelector('[data-download-menu]');
      const toggle = dropdown.querySelector('[data-download-toggle]');

      if (menu) {
        menu.hidden = true;
      }

      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  setDownloadLoading(trigger) {
    const button = trigger.matches('.yg-discover-design-resources__download-button, .yg-discover-design-resources__download-item')
      ? trigger
      : trigger.closest('.yg-discover-design-resources__download-button, .yg-discover-design-resources__download-item');

    if (!button) {
      return;
    }

    button.dataset.loading = 'true';
    button.setAttribute('aria-busy', 'true');

    window.setTimeout(() => {
      delete button.dataset.loading;
      button.removeAttribute('aria-busy');
    }, 1600);
  }

  clearDownloadLoading(trigger) {
    if (!trigger) {
      return;
    }

    delete trigger.dataset.loading;
    trigger.removeAttribute('aria-busy');
  }

  async handleDownloadLink(event, link) {
    event.preventDefault();
    this.setDownloadLoading(link);

    try {
      if (await this.canDownloadNatively(link)) {
        this.triggerNativeDownload(link);
      } else {
        await this.downloadLinkAsBlob(link);
      }
    } catch (error) {
      console.warn('Falling back to native download link.', error);
      this.triggerNativeDownload(link);
    } finally {
      this.clearDownloadLoading(link);
    }
  }

  async canDownloadNatively(link) {
    const href = link.getAttribute('href');

    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return false;
    }

    if (link.dataset.forceBlobDownload === 'true') {
      return false;
    }

    if (this.isSameOriginDownload(link.href)) {
      return true;
    }

    return this.hasAttachmentDisposition(link.href);
  }

  async hasAttachmentDisposition(url) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(url, {
        method: 'HEAD',
        mode: 'cors',
        credentials: 'omit',
        signal: controller.signal
      });
      const disposition = response.headers.get('content-disposition') || '';

      return /attachment/i.test(disposition);
    } catch (error) {
      return false;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  triggerNativeDownload(link) {
    const url = link.href;
    const fileName = link.getAttribute('download') || this.getDownloadNameFromUrl(url);

    if (!url) {
      return;
    }

    if (this.isSameOriginDownload(url)) {
      const temporaryLink = document.createElement('a');

      temporaryLink.href = url;
      temporaryLink.download = fileName;
      temporaryLink.rel = 'noopener';
      temporaryLink.style.display = 'none';
      document.body.appendChild(temporaryLink);
      temporaryLink.click();
      temporaryLink.remove();
      return;
    }

    const iframe = document.createElement('iframe');

    iframe.src = url;
    iframe.hidden = true;
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);
    window.setTimeout(() => iframe.remove(), 60000);
  }

  isSameOriginDownload(url) {
    try {
      return new URL(url, window.location.href).origin === window.location.origin;
    } catch (error) {
      return false;
    }
  }

  async downloadLinkAsBlob(link) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);

    try {
      const response = await fetch(link.href, {
        mode: 'cors',
        credentials: 'omit',
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Download request failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const temporaryLink = document.createElement('a');

      temporaryLink.href = objectUrl;
      temporaryLink.download = link.getAttribute('download') || this.getDownloadNameFromUrl(link.href);
      temporaryLink.style.display = 'none';
      document.body.appendChild(temporaryLink);
      temporaryLink.click();
      temporaryLink.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  async loadZipLibrary() {
    if (this.zipLibLoaded) {
      return this.zipLib;
    }

    return new Promise((resolve, reject) => {
      if (window.zip && window.zip.ZipWriter) {
        this.zipLib = window.zip;
        this.zipLibLoaded = true;
        resolve(this.zipLib);
        return;
      }

      const existingScript = document.querySelector('script[data-yg-script="zip-js"]');

      if (existingScript) {
        existingScript.addEventListener(
          'load',
          () => {
            if (window.zip && window.zip.ZipWriter) {
              this.zipLib = window.zip;
              this.zipLibLoaded = true;
              resolve(this.zipLib);
            } else {
              reject(new Error('Zip.js loaded but ZipWriter is missing'));
            }
          },
          { once: true }
        );
        existingScript.addEventListener('error', () => reject(new Error('Zip.js script failed to load')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@zip.js/zip.js/dist/zip.min.js';
      script.defer = true;
      script.dataset.ygScript = 'zip-js';
      script.onload = () => {
        if (window.zip && window.zip.ZipWriter) {
          this.zipLib = window.zip;
          this.zipLibLoaded = true;
          resolve(this.zipLib);
        } else {
          reject(new Error('Zip.js loaded but ZipWriter is missing'));
        }
      };
      script.onerror = () => reject(new Error('Zip.js script failed to load'));
      document.head.appendChild(script);
    });
  }

  async startBatchDownload() {
    if (this.selectedFiles.size === 0 || !this.floatDownloadButton) {
      return;
    }

    const originalButtonHtml = this.floatDownloadButton.innerHTML;
    this.floatDownloadButton.disabled = true;
    this.floatDownloadButton.innerHTML = '<span>PACKING ZIP...</span>';

    try {
      const isMobile = window.innerWidth < 700 || /Mobi|Android|iPhone/i.test(navigator.userAgent);
      const threshold = isMobile ? 80 * 1024 * 1024 : 300 * 1024 * 1024;
      const buckets = [];
      let currentBucket = [];
      let currentBucketSize = 0;

      this.selectedFiles.forEach((fileItem) => {
        const fileItems = Array.isArray(fileItem.files) && fileItem.files.length > 0 ? fileItem.files : [fileItem];

        fileItems.forEach((downloadItem) => {
          if (!downloadItem.url) {
            return;
          }

          const fileSize = Number.parseInt(downloadItem.size || '0', 10) || 0;

          if (currentBucketSize + fileSize > threshold && currentBucket.length > 0) {
            buckets.push(currentBucket);
            currentBucket = [downloadItem];
            currentBucketSize = fileSize;
            return;
          }

          currentBucket.push(downloadItem);
          currentBucketSize += fileSize;
        });
      });

      if (currentBucket.length > 0) {
        buckets.push(currentBucket);
      }

      const zipLib = await this.loadZipLibrary();

      for (let index = 0; index < buckets.length; index += 1) {
        const bucket = buckets[index];
        this.floatDownloadButton.innerHTML = `<span>PACKING ZIP (${index + 1}/${buckets.length})...</span>`;

        const zipWriter = new zipLib.ZipWriter(new zipLib.BlobWriter('application/zip'));

        for (const file of bucket) {
          try {
            const response = await fetch(file.url);

            if (!response.ok) {
              throw new Error('Fetch failed');
            }

            const blob = await response.blob();
            const entryName = this.buildZipEntryName(file);

            await zipWriter.add(entryName, new zipLib.BlobReader(blob));
          } catch (fileError) {
            console.error(`Failed to pack file ${file.title}:`, fileError);
          }
        }

        const zipBlob = await zipWriter.close();
        const downloadUrl = URL.createObjectURL(zipBlob);
        const temporaryLink = document.createElement('a');

        temporaryLink.href = downloadUrl;
        temporaryLink.download = `eureka-file-${String(index + 1).padStart(2, '0')}.zip`;
        temporaryLink.style.display = 'none';
        document.body.appendChild(temporaryLink);
        temporaryLink.click();
        temporaryLink.remove();

        window.setTimeout(() => {
          URL.revokeObjectURL(downloadUrl);
        }, 5000);
      }

      this.exitMultiSelectMode();
    } catch (error) {
      window.alert(`Batch download packing failed: ${error.message}`);
      console.error(error);
    } finally {
      this.floatDownloadButton.innerHTML = originalButtonHtml;
      this.updateSelectedCounter();
    }
  }

  getDownloadNameFromUrl(url) {
    try {
      const pathname = new URL(url, window.location.href).pathname;
      const fileName = pathname.split('/').filter(Boolean).pop();

      return decodeURIComponent(fileName || 'download');
    } catch (error) {
      return 'download';
    }
  }

  updateResultsCount(count = this.currentCards.length) {
    if (this.resultsCountElement) {
      this.resultsCountElement.textContent = `${count} results`;
    }

    this.querySelectorAll('[data-mobile-filter-results-count]').forEach((element) => {
      element.textContent = String(count);
    });
  }

  scrollResultsIntoView() {
    if (!this.resultsContainer) {
      return;
    }

    const top = this.resultsContainer.getBoundingClientRect().top + window.scrollY - 100;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: 'smooth'
    });
  }

  getUniqueProductIds(hits) {
    const seen = new Set();

    return (hits || []).reduce((accumulator, hit) => {
      const productId = String(hit.id || '');

      if (!productId || seen.has(productId)) {
        return accumulator;
      }

      seen.add(productId);
      accumulator.push(productId);
      return accumulator;
    }, []);
  }

  parseSelectedCardFiles(checkbox) {
    try {
      const files = JSON.parse(checkbox.dataset.cardFiles || '[]');

      if (Array.isArray(files) && files.length > 0) {
        return files
          .map((file) => ({
            url: this.normalizeString(file.url),
            size: Number.parseInt(file.size || '0', 10) || 0,
            title: this.normalizeString(file.title) || checkbox.dataset.productTitle || 'File',
            fileName: this.normalizeString(file.fileName) || this.getDownloadNameFromUrl(file.url)
          }))
          .filter((file) => file.url);
      }
    } catch (error) {
      return [];
    }

    return [];
  }

  async openPreviewFromTrigger(trigger) {
    const file = {
      url: trigger.dataset.previewUrl || '',
      title: trigger.dataset.previewTitle || 'Preview',
      fileName: trigger.dataset.previewFileName || '',
      fileType: trigger.dataset.previewFileType || '',
      mimeType: trigger.dataset.previewMimeType || ''
    };

    if (!file.url) {
      return;
    }

    if (this.resourceType === 'images') {
      const cardIndex = Number.parseInt(trigger.dataset.previewCardIndex || '-1', 10);
      this.openImageGalleryPreview(Number.isNaN(cardIndex) ? -1 : cardIndex, file);
      return;
    }

    if (this.isPdfFile(file)) {
      await this.openPdfPreview(file);
      return;
    }

    this.openGenericPreview(file);
  }

  openImageGalleryPreview(cardIndex, fallbackFile) {
    if (!this.previewImageGalleryContainer) {
      return;
    }

    const galleryCards = this.getCurrentPagePreviewCards();
    const safeIndex = cardIndex >= 0 && cardIndex < galleryCards.length
      ? cardIndex
      : galleryCards.findIndex((card) => card.file?.url === fallbackFile?.url);

    this.currentImagePreviewIndex = Math.max(safeIndex, 0);
    this.renderImageGalleryPreview();
    this.showPreviewState('image-gallery');
    this.openPreviewModal('image-gallery');
  }

  renderImageGalleryPreview() {
    if (!this.previewImageGalleryContainer) {
      return;
    }

    const galleryCards = this.getCurrentPagePreviewCards();
    const currentCard = galleryCards[this.currentImagePreviewIndex];

    if (!currentCard?.file?.url) {
      this.showPreviewState('error', this.previewErrorText);
      return;
    }

    const fileName = currentCard.fileName || currentCard.file.fileName || this.buildFileName(currentCard.title, currentCard.file.url);
    const hasPrevious = this.currentImagePreviewIndex > 0;
    const hasNext = this.currentImagePreviewIndex < galleryCards.length - 1;

    this.currentPreviewFile = currentCard.file;
    this.previewImageGalleryContainer.innerHTML = `
      <div class="yg-discover-design-resources__preview-image-stage">
        <img
          src="${this.escapeAttribute(currentCard.file.url)}"
          alt="${this.escapeAttribute(currentCard.imageAlt || currentCard.title || fileName)}"
          class="yg-discover-design-resources__preview-image"
        >
      </div>
      <div class="yg-discover-design-resources__preview-image-footer">
        <div class="yg-discover-design-resources__preview-image-copy">
          <p class="yg-discover-design-resources__preview-image-title">${this.escapeHtml(currentCard.title || fileName)}</p>
          <p class="yg-discover-design-resources__preview-image-subtitle">${this.escapeHtml(currentCard.subtitle || fileName)}</p>
        </div>
        <a
          class="yg-discover-design-resources__preview-image-download text-sm"
          href="${this.escapeAttribute(currentCard.file.url)}"
          download="${this.escapeAttribute(fileName)}"
          rel="noopener"
          aria-label="${this.escapeAttribute(`${this.downloadText} ${currentCard.title || fileName}`)}"
        >
          <svg class="yg-discover-design-resources__preview-image-download-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M10 3.125V12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            <path d="M6.25 9.375L10 13.125L13.75 9.375" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4.375 16.25H15.625" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          <span>${this.escapeHtml(this.downloadText)}</span>
        </a>
      </div>
    `;

    this.syncImagePreviewNavigation(hasPrevious, hasNext);
  }

  getCurrentPagePreviewCards() {
    return (this.currentPageCards || []).filter((card) => card?.file?.url);
  }

  navigateImagePreview(direction) {
    const galleryCards = this.getCurrentPagePreviewCards();

    if (galleryCards.length === 0) {
      return;
    }

    const nextIndex = this.currentImagePreviewIndex + direction;

    if (nextIndex < 0 || nextIndex >= galleryCards.length) {
      return;
    }

    this.currentImagePreviewIndex = nextIndex;
    this.renderImageGalleryPreview();
  }

  syncImagePreviewNavigation(hasPrevious, hasNext) {
    if (this.imagePreviewPreviousButton) {
      this.imagePreviewPreviousButton.disabled = !hasPrevious;
      this.imagePreviewPreviousButton.setAttribute('aria-disabled', hasPrevious ? 'false' : 'true');
    }

    if (this.imagePreviewNextButton) {
      this.imagePreviewNextButton.disabled = !hasNext;
      this.imagePreviewNextButton.setAttribute('aria-disabled', hasNext ? 'false' : 'true');
    }
  }

  openGenericPreview(file) {
    if (!this.previewGenericContainer) {
      return;
    }

    const previewType = this.getPreviewType(file);
    let previewMarkup = '';

    if (previewType === 'image') {
      previewMarkup = `
        <img
          src="${this.escapeAttribute(file.url)}"
          alt="${this.escapeAttribute(file.title || file.fileName || 'Preview image')}"
          class="yg-discover-design-resources__preview-generic-image"
        >
      `;
    } else if (previewType === 'video') {
      previewMarkup = `
        <video
          class="yg-discover-design-resources__preview-generic-video"
          controls
          playsinline
          preload="metadata"
        >
          <source src="${this.escapeAttribute(file.url)}" type="${this.escapeAttribute(file.mimeType || '')}">
        </video>
      `;
    } else {
      previewMarkup = `
        <iframe
          src="${this.escapeAttribute(file.url)}"
          class="yg-discover-design-resources__preview-generic-frame"
          title="${this.escapeAttribute(file.title || file.fileName || 'Preview file')}"
          loading="lazy"
          referrerpolicy="no-referrer"
        ></iframe>
      `;
    }

    this.currentPreviewFile = file;
    this.previewGenericContainer.innerHTML = previewMarkup;
    this.showPreviewState('generic');
    this.openPreviewModal('generic');
  }

  async openPdfPreview(file) {
    if (!this.previewPdfContainer || !this.pdfPreviewMain || !this.pdfPreviewThumbnails) {
      return;
    }

    this.currentPreviewFile = file;
    this.showPreviewState('loading');
    this.openPreviewModal('pdf');

    const requestId = ++this.previewRenderRequestId;

    try {
      const pdfjsLib = await this.loadPdfLibrary();

      if (requestId !== this.previewRenderRequestId) {
        return;
      }

      if (this.currentPdfLoadingTask) {
        await this.destroyCurrentPdfDocument();
      }

      const loadingTask = pdfjsLib.getDocument({
        url: file.url,
        withCredentials: false
      });

      this.currentPdfLoadingTask = loadingTask;
      const pdfDocument = await loadingTask.promise;

      if (requestId !== this.previewRenderRequestId) {
        pdfDocument.cleanup();
        return;
      }

      this.currentPdfDocument = pdfDocument;
      await this.renderPdfPreview(pdfDocument, requestId);
    } catch (error) {
      if (requestId !== this.previewRenderRequestId) {
        return;
      }

      console.error('Failed to render PDF preview.', error);
      this.showPreviewState('error', this.previewErrorText);
    }
  }

  async renderPdfPreview(pdfDocument, requestId) {
    if (!this.pdfPreviewMain || !this.pdfPreviewThumbnails) {
      return;
    }

    this.pdfPreviewMain.innerHTML = '';
    this.pdfPreviewThumbnails.innerHTML = '';

    const mainFragment = document.createDocumentFragment();
    const thumbnailFragment = document.createDocumentFragment();
    const pageWidth = this.getPdfPageWidth();
    const thumbnailWidth = this.getPdfThumbnailWidth();

    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
      const page = await pdfDocument.getPage(pageNumber);

      if (requestId !== this.previewRenderRequestId) {
        return;
      }

      const pageItem = document.createElement('div');
      pageItem.className = 'yg-discover-design-resources__preview-pdf-page';
      pageItem.dataset.previewPage = String(pageNumber);

      const pageCanvas = document.createElement('canvas');
      pageCanvas.className = 'yg-discover-design-resources__preview-pdf-canvas';
      pageCanvas.setAttribute('aria-label', `PDF page ${pageNumber}`);
      pageItem.appendChild(pageCanvas);
      mainFragment.appendChild(pageItem);

      const thumbnailButton = document.createElement('button');
      thumbnailButton.type = 'button';
      thumbnailButton.className = 'yg-discover-design-resources__preview-thumbnail';
      thumbnailButton.dataset.previewThumbnail = String(pageNumber);
      thumbnailButton.setAttribute('aria-label', `Preview page ${pageNumber}`);

      const thumbnailFrame = document.createElement('span');
      thumbnailFrame.className = 'yg-discover-design-resources__preview-thumbnail-frame';
      const thumbnailCanvas = document.createElement('canvas');
      thumbnailCanvas.className = 'yg-discover-design-resources__preview-thumbnail-canvas';
      thumbnailFrame.appendChild(thumbnailCanvas);
      thumbnailButton.appendChild(thumbnailFrame);

      const thumbnailIndex = document.createElement('span');
      thumbnailIndex.className = 'yg-discover-design-resources__preview-thumbnail-index';
      thumbnailIndex.textContent = String(pageNumber);
      thumbnailButton.appendChild(thumbnailIndex);
      thumbnailFragment.appendChild(thumbnailButton);

      await this.renderPdfPageToCanvas(page, pageCanvas, pageWidth);
      await this.renderPdfPageToCanvas(page, thumbnailCanvas, thumbnailWidth);
    }

    if (requestId !== this.previewRenderRequestId) {
      return;
    }

    this.pdfPreviewMain.appendChild(mainFragment);
    this.pdfPreviewThumbnails.appendChild(thumbnailFragment);
    this.showPreviewState('pdf');
    this.setActivePreviewPage(1);
    this.pdfPreviewMain.scrollTop = 0;
  }

  async renderPdfPageToCanvas(page, canvas, targetWidth) {
    const unscaledViewport = page.getViewport({ scale: 1 });
    const scale = targetWidth / unscaledViewport.width;
    const viewport = page.getViewport({ scale });
    const outputScale = window.devicePixelRatio || 1;
    const context = canvas.getContext('2d');

    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;

    await page.render({
      canvasContext: context,
      transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null,
      viewport
    }).promise;
  }

  async loadPdfLibrary() {
    if (YGDiscoverDesignResources.pdfLibraryPromise) {
      return YGDiscoverDesignResources.pdfLibraryPromise;
    }

    YGDiscoverDesignResources.pdfLibraryPromise = import('https://cdn.jsdelivr.net/npm/pdfjs-dist@6.0.227/build/pdf.min.mjs')
      .then((module) => {
        const pdfjsLib = module?.GlobalWorkerOptions ? module : module?.default;

        if (!pdfjsLib?.GlobalWorkerOptions) {
          throw new Error('PDF.js library failed to load.');
        }

        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@6.0.227/build/pdf.worker.min.mjs';
        return pdfjsLib;
      })
      .catch((error) => {
        YGDiscoverDesignResources.pdfLibraryPromise = null;
        throw error;
      });

    return YGDiscoverDesignResources.pdfLibraryPromise;
  }

  openPreviewModal(type) {
    if (!this.previewModal) {
      return;
    }

    this.previewModal.hidden = false;
    this.previewModal.dataset.open = 'true';
    this.previewModal.dataset.previewType = type;
    this.bodyOverflowBeforePreview = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  closePreviewModal(options = {}) {
    const { preserveBodyOverflow = true } = options;

    this.previewRenderRequestId += 1;

    if (this.previewModal) {
      this.previewModal.hidden = true;
      this.previewModal.dataset.open = 'false';
      delete this.previewModal.dataset.previewType;
    }

    if (this.previewGenericContainer) {
      this.previewGenericContainer.innerHTML = '';
      this.previewGenericContainer.hidden = true;
    }

    if (this.previewPdfContainer) {
      this.previewPdfContainer.hidden = true;
    }

    if (this.previewImageGalleryContainer) {
      this.previewImageGalleryContainer.innerHTML = '';
      this.previewImageGalleryContainer.hidden = true;
    }

    this.syncImagePreviewNavigation(false, false);

    if (this.previewLoadingState) {
      this.previewLoadingState.hidden = true;
    }

    if (this.previewErrorState) {
      this.previewErrorState.hidden = true;
      this.previewErrorState.textContent = '';
    }

    if (this.pdfPreviewMain) {
      this.pdfPreviewMain.innerHTML = '';
      this.pdfPreviewMain.scrollTop = 0;
    }

    if (this.pdfPreviewThumbnails) {
      this.pdfPreviewThumbnails.innerHTML = '';
    }

    void this.destroyCurrentPdfDocument();
    this.currentPreviewFile = null;
    this.currentImagePreviewIndex = -1;

    if (preserveBodyOverflow) {
      document.body.style.overflow = this.bodyOverflowBeforePreview;
    }
  }

  async destroyCurrentPdfDocument() {
    const loadingTask = this.currentPdfLoadingTask;
    const pdfDocument = this.currentPdfDocument;

    this.currentPdfLoadingTask = null;
    this.currentPdfDocument = null;

    if (loadingTask && typeof loadingTask.destroy === 'function') {
      try {
        await loadingTask.destroy();
      } catch (error) {
        // Ignore cleanup errors from aborted render tasks.
      }
    }

    if (pdfDocument && typeof pdfDocument.cleanup === 'function') {
      try {
        await Promise.resolve(pdfDocument.cleanup());
      } catch (error) {
        // Ignore cleanup errors from already released documents.
      }
    }
  }

  showPreviewState(state, message = '') {
    if (this.previewLoadingState) {
      this.previewLoadingState.hidden = state !== 'loading';
      this.previewLoadingState.textContent = this.previewLoadingText;
    }

    if (this.previewErrorState) {
      this.previewErrorState.hidden = state !== 'error';
      this.previewErrorState.textContent = message;
    }

    if (this.previewGenericContainer) {
      this.previewGenericContainer.hidden = state !== 'generic';
    }

    if (this.previewPdfContainer) {
      this.previewPdfContainer.hidden = state !== 'pdf';
    }

    if (this.previewImageGalleryContainer) {
      this.previewImageGalleryContainer.hidden = state !== 'image-gallery';
    }
  }

  scrollToPdfPage(pageNumber) {
    if (!this.pdfPreviewMain) {
      return;
    }

    const targetPage = this.pdfPreviewMain.querySelector(`[data-preview-page="${pageNumber}"]`);

    if (!targetPage) {
      return;
    }

    this.pdfPreviewMain.scrollTo({
      top: targetPage.offsetTop,
      behavior: 'smooth'
    });
    this.setActivePreviewPage(pageNumber);
  }

  setActivePreviewPage(pageNumber) {
    this.querySelectorAll('[data-preview-thumbnail]').forEach((thumbnail) => {
      const isActive = Number.parseInt(thumbnail.dataset.previewThumbnail || '0', 10) === pageNumber;

      thumbnail.classList.toggle('is-active', isActive);
      if (isActive) {
        thumbnail.setAttribute('aria-current', 'true');
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    });
  }

  isPreviewOpen() {
    return this.previewModal?.dataset.open === 'true';
  }

  getPreviewType(file) {
    if (this.isPdfFile(file)) {
      return 'pdf';
    }

    if (this.isImageFile(file)) {
      return 'image';
    }

    if (this.isVideoFile(file)) {
      return 'video';
    }

    return 'iframe';
  }

  isPdfFile(file) {
    const mimeType = this.normalizeString(file?.mimeType).toLowerCase();
    const fileType = this.normalizeString(file?.fileType).toLowerCase();
    const extension = this.extractFileExtension(file?.fileName || file?.url);

    return mimeType.includes('pdf') || fileType === 'pdf' || extension === 'pdf';
  }

  isImageFile(file) {
    const mimeType = this.normalizeString(file?.mimeType).toLowerCase();
    const extension = this.extractFileExtension(file?.fileName || file?.url);
    const imageExtensions = ['avif', 'bmp', 'gif', 'heic', 'heif', 'jpeg', 'jpg', 'png', 'svg', 'webp'];

    return mimeType.startsWith('image/') || imageExtensions.includes(extension);
  }

  isVideoFile(file) {
    const mimeType = this.normalizeString(file?.mimeType).toLowerCase();
    const extension = this.extractFileExtension(file?.fileName || file?.url);
    const videoExtensions = ['mp4', 'webm', 'mov', 'm4v', 'ogv'];

    return mimeType.startsWith('video/') || videoExtensions.includes(extension);
  }

  getPdfPageWidth() {
    const maxWidth = this.isMobileLayout() ? 330 : 520;
    const availableWidth = this.pdfPreviewMain?.clientWidth || maxWidth;

    return Math.max(Math.min(availableWidth, maxWidth), 220);
  }

  getPdfThumbnailWidth() {
    return this.isMobileLayout() ? 120 : 120;
  }

  getQuerySignature() {
    return this.searchInput?.value || '';
  }

  getSearchQueryWords() {
    return this.normalizeSearchText(this.getQuerySignature())
      .split(' ')
      .filter(Boolean);
  }

  normalizeSearchText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
  }

  normalizeFacetValue(value) {
    return String(value || '').trim().toLowerCase();
  }

  extractNumericId(gid) {
    return String(gid || '').split('/').pop();
  }

  getProductCacheKey(productId) {
    return `${this.resourceType}:${productId}`;
  }

  normalizeString(value) {
    return String(value || '').trim();
  }

  getFileUrl(field) {
    return this.normalizeString(field?.reference?.url) || this.normalizeString(field?.value);
  }

  getFileNameFromUrl(url) {
    return this.getDownloadNameFromUrl(url);
  }

  getImageFileName(fields, url, fallbackTitle) {
    return this.getFileNameFromUrl(url)
      || this.buildFileName(fallbackTitle, url, '');
  }

  getFileTypeFromName(name) {
    return this.getActualFileType({
      fileName: name,
      url: name
    });
  }

  getActualFileType({ fileName = '', url = '', mimeType = '' } = {}) {
    const extension = this.extractFileExtension(fileName || url);

    if (extension) {
      return this.getFileTypeLabel(extension);
    }

    const normalizedMimeType = this.normalizeString(mimeType).toLowerCase();

    if (!normalizedMimeType) {
      return '';
    }

    const mimeSubtype = normalizedMimeType.split('/').pop()?.split(';')[0]?.trim() || '';

    return this.getFileTypeLabel(mimeSubtype);
  }

  getFileTypeLabel(value) {
    const normalizedValue = this.normalizeString(value).toLowerCase();

    if (!normalizedValue) {
      return '';
    }

    const fileTypeMap = {
      avif: 'AVIF',
      bmp: 'BMP',
      gif: 'GIF',
      heic: 'HEIC',
      heif: 'HEIF',
      jpeg: 'JPG',
      jpg: 'JPG',
      json: 'JSON',
      mov: 'MOV',
      mp4: 'MP4',
      m4v: 'M4V',
      ogv: 'OGV',
      pdf: 'PDF',
      png: 'PNG',
      svg: 'SVG',
      tif: 'TIF',
      tiff: 'TIFF',
      txt: 'TXT',
      webm: 'WEBM',
      webp: 'WEBP',
      xls: 'XLS',
      xlsx: 'XLSX',
      zip: 'ZIP'
    };

    return fileTypeMap[normalizedValue] || normalizedValue.toUpperCase();
  }

  isModelResourceType() {
    return this.resourceType === 'autocad' || this.resourceType === 'revit';
  }

  getUniqueValues(values) {
    const seen = new Set();

    return (values || []).reduce((result, value) => {
      const normalizedValue = this.normalizeString(value);

      if (!normalizedValue || seen.has(normalizedValue)) {
        return result;
      }

      seen.add(normalizedValue);
      result.push(normalizedValue);
      return result;
    }, []);
  }

  getCardDownloadFiles(card) {
    if (Array.isArray(card?.files) && card.files.length > 0) {
      return card.files.filter((file) => file?.url);
    }

    return card?.file?.url ? [card.file] : [];
  }

  parseDisplayDate(value) {
    const timestamp = Date.parse(String(value || '').trim());

    return Number.isNaN(timestamp) ? null : timestamp;
  }

  compareDateValues(valueA, valueB, direction = 1) {
    if (valueA === null && valueB === null) {
      return 0;
    }

    if (valueA === null) {
      return 1;
    }

    if (valueB === null) {
      return -1;
    }

    return direction * (valueA - valueB);
  }

  getFileSizeBytes(value) {
    const text = String(value || '').trim();
    const match = text.match(/^([\d.]+)\s*(b|kb|mb|gb)?$/i);

    if (!match) {
      return 0;
    }

    const amount = Number.parseFloat(match[1]);
    const unit = (match[2] || 'b').toLowerCase();
    const multipliers = {
      b: 1,
      kb: 1024,
      mb: 1024 * 1024,
      gb: 1024 * 1024 * 1024
    };

    return Math.round(amount * (multipliers[unit] || 1));
  }

  getFileKey(url, fileName) {
    return `${url || ''}::${fileName || ''}`;
  }

  getCardKey(card, index) {
    return `${this.currentPage}:${index}:${card.file?.url || ''}:${card.fileName || ''}:${card.title || ''}`;
  }

  buildZipEntryName(file) {
    const rawName = file.fileName || this.buildFileName(file.title, file.url);
    const parts = rawName.split('.');
    const extension = parts.length > 1
      ? `.${parts.pop()}`
      : this.getFileExtension(file.url, this.normalizeString(file.fileType).toLowerCase());
    const baseName = parts.join('.') || file.title || 'file';
    const safeBaseName = String(baseName)
      .replace(/[^a-zA-Z0-9-_]+/g, '_')
      .replace(/^_+|_+$/g, '');

    return `${safeBaseName || 'file'}${extension}`;
  }

  chunkArray(items, size) {
    const chunks = [];

    for (let index = 0; index < items.length; index += size) {
      chunks.push(items.slice(index, index + size));
    }

    return chunks;
  }

  buildFileName(name, url, fallbackExtension = 'pdf') {
    const extension = this.getFileExtension(url, fallbackExtension);
    const safeName = String(name || 'download')
      .trim()
      .replace(/\.[a-z0-9]+$/i, '')
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();

    return `${safeName || 'download'}${extension}`;
  }

  extractFileExtension(value) {
    const rawValue = String(value || '').trim();

    if (!rawValue) {
      return '';
    }

    let pathname = rawValue.split('?')[0].split('#')[0].trim();

    try {
      pathname = new URL(rawValue, window.location.href).pathname || pathname;
    } catch (error) {
      pathname = pathname;
    }

    const lastSegment = pathname.split('/').filter(Boolean).pop() || pathname;
    const parts = lastSegment.split('.');

    if (parts.length < 2) {
      return '';
    }

    const extension = String(parts.pop() || '').toLowerCase();

    return /^[a-z0-9]{1,8}$/.test(extension) ? extension : '';
  }

  getFileExtension(url, fallbackExtension = 'pdf') {
    const extension = this.extractFileExtension(url);

    if (extension) {
      return `.${extension.toLowerCase()}`;
    }

    const normalizedFallback = this.normalizeString(fallbackExtension).replace(/^\./, '').toLowerCase();

    return normalizedFallback ? `.${normalizedFallback}` : '';
  }

  async resolveMissingCardFileTypes(cards, requestId = this.renderRequestId) {
    const lookupTasks = (cards || [])
      .filter((card) => card && !this.normalizeString(card.fileType))
      .map((card) => this.resolveCardFileType(card, requestId));

    if (lookupTasks.length === 0) {
      return;
    }

    const results = await Promise.allSettled(lookupTasks);
    const hasUpdatedCard = results.some((result) => result.status === 'fulfilled' && result.value === true);

    if (!hasUpdatedCard || requestId !== this.renderRequestId) {
      return;
    }

    this.scheduleFileTypeRefresh();
  }

  async resolveCardFileType(card, requestId = this.renderRequestId) {
    const fileUrl = this.normalizeString(card?.file?.url || card?.imageUrl);

    if (!fileUrl || requestId !== this.renderRequestId) {
      return false;
    }

    const remoteMimeType = await this.getRemoteMimeType(fileUrl);

    if (!remoteMimeType || requestId !== this.renderRequestId) {
      return false;
    }

    const resolvedFileType = this.getActualFileType({
      fileName: card.fileName,
      url: fileUrl,
      mimeType: remoteMimeType
    });

    if (!resolvedFileType) {
      return false;
    }

    card.fileType = this.normalizeString(resolvedFileType);
    card.fileName = this.extractFileExtension(card.fileName)
      ? card.fileName
      : this.buildFileName(card.title, fileUrl, card.fileType.toLowerCase());
    card.fileMimeType = this.normalizeString(card.fileMimeType || remoteMimeType);
    card.searchableText = this.getCardSearchText(card);

    if (card.file) {
      card.file.fileName = this.extractFileExtension(card.file.fileName)
        ? card.file.fileName
        : this.buildFileName(card.file.title || card.title, fileUrl, card.fileType.toLowerCase());
      card.file.mimeType = this.normalizeString(card.file.mimeType || remoteMimeType);
      card.file.fileType = card.fileType;
    }

    return true;
  }

  async getRemoteMimeType(url) {
    const normalizedUrl = this.normalizeString(url);

    if (!normalizedUrl) {
      return '';
    }

    if (this.remoteMimeTypeCache.has(normalizedUrl)) {
      return this.remoteMimeTypeCache.get(normalizedUrl) || '';
    }

    if (!this.fileTypeLookupPromises.has(normalizedUrl)) {
      const lookupPromise = this.fetchRemoteMimeType(normalizedUrl)
        .catch(() => '')
        .then((mimeType) => {
          const normalizedMimeType = this.normalizeString(mimeType);

          this.remoteMimeTypeCache.set(normalizedUrl, normalizedMimeType);
          this.fileTypeLookupPromises.delete(normalizedUrl);

          return normalizedMimeType;
        });

      this.fileTypeLookupPromises.set(normalizedUrl, lookupPromise);
    }

    return this.fileTypeLookupPromises.get(normalizedUrl);
  }

  async fetchRemoteMimeType(url) {
    const response = await fetch(url, {
      method: 'HEAD',
      cache: 'force-cache'
    });

    if (!response.ok) {
      return '';
    }

    return response.headers.get('content-type') || '';
  }

  scheduleFileTypeRefresh() {
    if (this.fileTypeRefreshFrame) {
      return;
    }

    this.fileTypeRefreshFrame = window.requestAnimationFrame(() => {
      this.fileTypeRefreshFrame = 0;
      this.applySearchFilter();
      this.applyFacets();
      this.renderFacets();
    });
  }

  cancelFileTypeRefresh() {
    if (!this.fileTypeRefreshFrame) {
      return;
    }

    window.cancelAnimationFrame(this.fileTypeRefreshFrame);
    this.fileTypeRefreshFrame = 0;
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }

  get algoliaAppId() {
    return this.dataset.algoliaAppId || '';
  }

  get algoliaSearchKey() {
    return this.dataset.algoliaSearchKey || '';
  }

  get algoliaIndexName() {
    return this.dataset.algoliaIndexName || 'shopify_products';
  }

  get algoliaScanPageSize() {
    return Math.min(Math.max(Number.parseInt(this.dataset.algoliaScanPageSize || '250', 10), 1), 1000);
  }

  get storefrontDomain() {
    return this.dataset.storefrontDomain || 'eureka-business.myshopify.com';
  }

  get storefrontToken() {
    return this.dataset.storefrontToken || '';
  }

  get storefrontApiVersion() {
    return this.dataset.storefrontApiVersion || '2024-04';
  }

  get storefrontEndpoint() {
    return `https://${this.storefrontDomain}/api/${this.storefrontApiVersion}/graphql.json`;
  }

  get resourceType() {
    return this.dataset.resourceType || 'documents';
  }

  get resourceMetafieldKeys() {
    const fallbackKey = this.resourceType === 'images' ? 'yg_image_card' : 'yg_document_download_card';
    return (this.dataset.resourceMetafieldKey || fallbackKey)
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);
  }

  get searchPlaceholder() {
    return this.dataset.searchPlaceholder || 'Search Options';
  }

  get showMoreText() {
    return this.dataset.showMoreText || 'Show More';
  }

  get showLessText() {
    return this.dataset.showLessText || 'Show Less';
  }

  get loadingText() {
    return this.dataset.loadingText || 'Loading resources...';
  }

  get noResultsText() {
    return this.dataset.noResultsText || 'No resources found.';
  }

  get errorText() {
    return this.dataset.errorText || 'Something went wrong. Please try again later.';
  }

  get hideFiltersText() {
    return this.dataset.hideFiltersText || 'Hide Filters';
  }

  get showFiltersText() {
    return this.dataset.showFiltersText || 'Show Filters';
  }

  get downloadText() {
    return this.dataset.downloadText || 'Download';
  }

  get previousText() {
    return this.dataset.previousText || 'Previous';
  }

  get nextText() {
    return this.dataset.nextText || 'Next';
  }

  get previewText() {
    return this.dataset.previewText || 'Preview';
  }

  get previewLoadingText() {
    return this.dataset.previewLoadingText || 'Loading preview...';
  }

  get previewErrorText() {
    return this.dataset.previewErrorText || 'Preview unavailable. Please try again later.';
  }

  get pageSize() {
    return Math.max(Number.parseInt(this.dataset.pageSize || '12', 10), 1);
  }

  get searchBoxContainer() {
    return this.querySelector('[data-search-box]');
  }

  get searchInput() {
    return this.querySelector('[data-resource-search-input]');
  }

  get searchForm() {
    return this.searchBoxContainer?.querySelector('.ais-SearchBox-form');
  }

  get searchResetButton() {
    return this.searchBoxContainer?.querySelector('.ais-SearchBox-reset');
  }

  get resourceTypeContainer() {
    return this.querySelector('[data-resource-type-refinement]');
  }

  get fileTypeContainer() {
    return this.querySelector('[data-file-type-refinement]');
  }

  get productTypeContainer() {
    return this.querySelector('[data-product-type-refinement]');
  }

  get productTypeGroups() {
    if (this.cachedProductTypeGroups) {
      return this.cachedProductTypeGroups;
    }

    try {
      const groups = JSON.parse(this.dataset.productTypeGroups || '[]');

      this.cachedProductTypeGroups = Array.isArray(groups)
        ? groups
          .map((group) => ({
            label: this.normalizeString(group.label),
            children: Array.isArray(group.children) ? group.children.map((item) => this.normalizeString(item)).filter(Boolean) : []
          }))
          .filter((group) => group.label && group.children.length > 0)
        : [];
    } catch (error) {
      this.cachedProductTypeGroups = [];
    }

    return this.cachedProductTypeGroups;
  }

  get cardsContainer() {
    return this.querySelector('[data-resources-cards]');
  }

  get paginationContainer() {
    return this.querySelector('[data-resources-pagination]');
  }

  get resultsContainer() {
    return this.querySelector('[data-resources-results]');
  }

  get filtersContainer() {
    return this.querySelector('[data-resources-filters]');
  }

  get resultsCountElement() {
    return this.querySelector('[data-results-count]');
  }

  get gridScaleSlider() {
    return this.querySelector('[data-grid-scale-slider]');
  }

  get multiSelectToggle() {
    return this.querySelector('[data-multi-select-trigger]');
  }

  get floatPanel() {
    return this.querySelector('[data-float-panel]');
  }

  get floatSelectAll() {
    return this.querySelector('[data-float-select-all-trigger]');
  }

  get floatDownloadButton() {
    return this.querySelector('[data-float-download-trigger]');
  }

  get previewModal() {
    return this.querySelector('[data-preview-modal]');
  }

  get previewLoadingState() {
    return this.querySelector('[data-preview-loading]');
  }

  get previewErrorState() {
    return this.querySelector('[data-preview-error]');
  }

  get previewGenericContainer() {
    return this.querySelector('[data-preview-generic]');
  }

  get previewPdfContainer() {
    return this.querySelector('[data-preview-pdf]');
  }

  get previewImageGalleryContainer() {
    return this.querySelector('[data-preview-image-gallery]');
  }

  get imagePreviewPreviousButton() {
    return this.querySelector('[data-image-preview-nav="previous"]');
  }

  get imagePreviewNextButton() {
    return this.querySelector('[data-image-preview-nav="next"]');
  }

  get pdfPreviewThumbnails() {
    return this.querySelector('[data-preview-thumbnails]');
  }

  get pdfPreviewMain() {
    return this.querySelector('[data-preview-main]');
  }
}

if (!window.customElements.get('yg-discover-design-resources')) {
  window.customElements.define('yg-discover-design-resources', YGDiscoverDesignResources);
}
